import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Checkbox,
  FormControlLabel,
  TextField,
  Paper,
  Typography,
  Alert,
  Snackbar,
  MenuItem,
  Stack,
  CircularProgress,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  useTheme,
  Avatar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import {
  getOrder,
  createOrder,
  createOrderFromSource,
  importOrderDataInto,
  updateOrder,
  addOrderLine,
  addOrderLineItem,
  deleteOrderLine,
  updateOrderLineItem,
  deleteOrderLineItem,
  upsertOrderLineItemCustomizationFields,
  deleteOrderLineItemCustomizationField,
  getCustomers,
  getProducts,
  getSKUs,
  createCustomer,
  getSettings,
  uploadMedia,
  getMedias,
  deleteOrderStatusHistory,
  listOrderTypes,
  changeOrderType,
  getOrderTypeStatuses,
  createOrderPaymentEntry,
  updateOrderPaymentEntry,
  deleteOrderPaymentEntry,
  openOrderPaymentEntryReceipt,
  softDeleteOrder,
} from '../app/api';
import ConfirmDialog from '../components/ConfirmDialog';
import MediaGallery, { PRODUCT_DEFAULT_IMAGE } from '../components/MediaGallery';
import MediaLightbox from '../components/MediaLightbox';
import { OrderSearchAutocomplete } from '../components/OrderSearchAutocomplete';
import { SavingOverlay, InlineSavingOverlay } from '../components/SavingOverlay';
import OrderFormItemDescriptionField from '../order/OrderFormItemDescriptionField';
import { useBusyCount } from '../hooks/useBusyCount';
import { useTranslation } from '../app/LanguageContext';
import { useWorkspace } from '../app/WorkspaceContext';
import { usePageTitle } from '../layout/PageTitleContext';
import { formatCurrency, handleCurrencyInputChange, parseCurrency, handlePercentInputChange, parsePercent, formatPercent } from '../app/currency';
import { addBusinessDays, formatDateISO, startOfLocalDay, expectedDeliveryISOFromCreatedAt } from '../app/businessDays';
import {
  isoToDisplayBR,
  displayBRToISO,
  handleDateInputChange,
  formatDate,
  formatDateToISO,
} from '../app/date';
import OrderHistoryTimeline from '../order/OrderHistoryTimeline';
import OrderGalleryManagerDialog from '../order/OrderGalleryManagerDialog';
import WorkflowEditorPage from './WorkflowEditorPage';
import { buildOrderStatusTimelineEvents } from '../order/orderHistoryUtil';
import { buildOrderUpdatePayloadFromFormData } from '../order/buildOrderUpdatePayload';
import { migrateOrderDraftMediaToHistory } from '../order/migrateOrderDraftMediaToHistory';
import { getLastUsedOrderTypeId, setLastUsedOrderTypeId, pickActiveOrderTypeId } from '../order/lastOrderTypeStorage';
import { getOrderThemeLayoutSx } from '../order/orderMockLayoutSx';
import { ExtensionSlot } from '../components/ExtensionSlot';

function getCustomerOptionLabel(option) {
  if (typeof option === 'string') return option;
  return (option.name || '') + (option.email ? ' – ' + option.email : '');
}

const ORDER_FORM_API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const ORDER_PAYMENT_METHOD_OPTIONS = [
  { value: 'PIX', labelKey: 'orders.payment_method_pix' },
  { value: 'CREDIT_CARD', labelKey: 'orders.payment_method_credit_card' },
  { value: 'DEBIT_CARD', labelKey: 'orders.payment_method_debit_card' },
  { value: 'CASH', labelKey: 'orders.payment_method_cash' },
  { value: 'BANK_TRANSFER', labelKey: 'orders.payment_method_bank_transfer' },
  { value: 'OTHER', labelKey: 'orders.payment_method_other' },
];

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function paymentMethodLabelFromValue(translate, methodValue) {
  const opt = ORDER_PAYMENT_METHOD_OPTIONS.find((o) => o.value === methodValue);
  return opt ? translate(opt.labelKey) : methodValue;
}

/** Form used inside Dialog for create/edit payment entry. onSubmit receives { body, receiptFile }; parent runs request and closes dialog. */
function OrderPaymentEntryForm({ mode, entry, orderId, translate, notify, onSubmit, onCancel, onRequestDelete }) {
  const isEdit = mode === 'edit';
  const fileInputRef = useRef(null);
  const [method, setMethod] = useState('PIX');
  const [paidAt, setPaidAt] = useState(todayISO());
  const [amountNum, setAmountNum] = useState(0);
  const [amountDisplay, setAmountDisplay] = useState('');
  const [notes, setNotes] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [clearReceipt, setClearReceipt] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit && entry) {
      setMethod(entry.method);
      setPaidAt(entry.paid_at || '');
      setAmountNum(Number(entry.amount) || 0);
      setAmountDisplay(entry.amount > 0 ? formatCurrency(Number(entry.amount)) : '');
      setNotes(entry.notes || '');
    } else {
      setMethod('PIX');
      setPaidAt(todayISO());
      setAmountNum(0);
      setAmountDisplay('');
      setNotes('');
    }
    setReceiptFile(null);
    setClearReceipt(false);
  }, [isEdit, entry]);

  const savedReceipt = isEdit && entry?.receipt?.available && !clearReceipt && !receiptFile;
  const legacyReceiptHref =
    savedReceipt && entry.receipt.legacy_file_path ? `${ORDER_FORM_API_BASE}${entry.receipt.legacy_file_path}` : null;

  const onReceiptFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setReceiptFile(f);
    setClearReceipt(false);
  };

  const handleSubmit = async () => {
    if (amountNum <= 0) {
      notify(translate('orders.payment_amount_invalid'), 'error');
      return;
    }
    setSaving(true);
    try {
      const body = { method, paid_at: paidAt, amount: amountNum, notes: notes.trim() };
      if (clearReceipt && !receiptFile) {
        body.clear_receipt = true;
      }
      await onSubmit({ body, receiptFile });
    } catch (e) {
      notify(e.message || translate('common.error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5, minWidth: { xs: '100%', sm: 320 } }}>
      <input ref={fileInputRef} type="file" hidden onChange={onReceiptFile} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label={translate('orders.payment_method')}
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          {ORDER_PAYMENT_METHOD_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {translate(o.labelKey)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="date"
          label={translate('orders.payment_date')}
          value={paidAt}
          onChange={(e) => setPaidAt(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          size="small"
          label={translate('orders.payment_amount')}
          value={amountDisplay}
          onChange={(e) =>
            handleCurrencyInputChange(e, (synth) => {
              if (typeof synth === 'string') {
                setAmountDisplay('');
                setAmountNum(0);
                return;
              }
              setAmountDisplay(synth.target.value);
              const n = synth.target.numericValue;
              setAmountNum(typeof n === 'number' && !Number.isNaN(n) ? n : 0);
            })
          }
          placeholder="R$ 0,00"
          sx={{ minWidth: 140 }}
        />
      </Stack>
      <TextField
        size="small"
        fullWidth
        label={translate('orders.payment_notes')}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        multiline
        minRows={2}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        {savedReceipt && entry.receipt.download_path && orderId && (
          <Button
            size="small"
            onClick={() => {
              openOrderPaymentEntryReceipt(orderId, entry.id).catch((err) =>
                notify(err.message || translate('common.error'), 'error'),
              );
            }}
          >
            {translate('orders.payment_view_receipt')}
          </Button>
        )}
        {legacyReceiptHref && (
          <Button size="small" href={legacyReceiptHref} target="_blank" rel="noopener noreferrer">
            {translate('orders.payment_view_receipt')}
          </Button>
        )}
        {receiptFile && (
          <Typography variant="caption" color="text.secondary">
            {receiptFile.name}
          </Typography>
        )}
        {(savedReceipt || receiptFile) && (
          <Button
            size="small"
            color="warning"
            onClick={() => {
              if (receiptFile) {
                setReceiptFile(null);
                return;
              }
              if (entry?.receipt?.available) {
                setClearReceipt(true);
              }
            }}
          >
            {translate('orders.payment_remove_receipt')}
          </Button>
        )}
        {!savedReceipt && !receiptFile && (
          <Button size="small" variant="outlined" onClick={() => fileInputRef.current?.click()}>
            {translate('orders.payment_upload_receipt')}
          </Button>
        )}
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 1 }}>
        <Button onClick={onCancel}>{translate('common.cancel')}</Button>
        {isEdit && onRequestDelete && (
          <Button color="error" variant="outlined" onClick={onRequestDelete}>
            {translate('orders.payment_delete')}
          </Button>
        )}
        <Button variant="contained" disabled={saving} onClick={handleSubmit}>
          {saving ? translate('common.saving') : translate('common.save')}
        </Button>
      </Stack>
    </Box>
  );
}

const LEGACY_STATUS_ORDER = ['DRAFT', 'SENT', 'AWAITING_FORM', 'APPROVED', 'CONFIRMED', 'IN_PREPARATION', 'DELIVERED', 'REJECTED', 'EXPIRED', 'CANCELLED'];

function collectDistinctStatusIdsFromOrder(order) {
  const set = new Set();
  const add = (s) => {
    if (s == null || s === '') return;
    const t = String(s).trim();
    if (t) set.add(t);
  };
  if (order) {
    add(order.status);
    (order.status_history || []).forEach((h) => {
      add(h.status);
      if (h.previous_status) add(h.previous_status);
    });
  }
  return Array.from(set);
}

function sourceStatusLabelForMap(order, statusId) {
  if (!order) return statusId;
  if (String(order.status) === String(statusId)) return order.status_label || statusId;
  const h = (order.status_history || []).find((x) => String(x.status) === String(statusId));
  return h?.status_label || statusId;
}

const initialOrderTypeChangeDialog = {
  open: false,
  step: 1,
  targetTypeId: '',
  statusMap: {},
  targetStatuses: [],
  reason: 'change',
};

const DEFAULT_ITEM_CUSTOM_FIELD_DRAFT = { name: '', value_type: 'text', value: '', hide_in_public_form: false };

function parseCustomizationImageIds(raw) {
  if (raw == null || String(raw).trim() === '') return [];
  try {
    const v = JSON.parse(String(raw));
    return Array.isArray(v) ? v.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function stringifyCustomizationImageIds(ids) {
  return JSON.stringify(ids || []);
}

function normalizeMediaFilePath(path) {
  if (path == null || String(path).trim() === '') return '';
  const s = String(path).trim();
  return s.startsWith('/') ? s : `/${s}`;
}

function mimeTypeFromImagePath(path) {
  const lower = String(path).toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  return 'image/jpeg';
}

function filenameFromMediaPath(path) {
  const s = normalizeMediaFilePath(path);
  const i = s.lastIndexOf('/');
  const base = i >= 0 ? s.slice(i + 1) : s;
  return base || 'imagem';
}

function buildCustomizationImageLightboxItems(field, rawValue, pathById) {
  const ids = parseCustomizationImageIds(rawValue);
  const items = [];
  for (const mid of ids) {
    const asset = (field.image_assets || []).find((a) => a.id === mid);
    const pathRaw = asset?.url || pathById[mid] || '';
    const file_path = normalizeMediaFilePath(pathRaw);
    if (!file_path) continue;
    items.push({
      id: mid,
      file_path,
      filename: filenameFromMediaPath(file_path),
      mime_type: mimeTypeFromImagePath(file_path),
      file_size: 0,
    });
  }
  return items;
}

function newOrderFormItemRowKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `rk_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

function orderLinesBySequence(lines) {
  return [...(lines || [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
}

function appendUnsavedExtraFormItemRows(serverRows, currentFormItems) {
  const drafts = (currentFormItems || []).filter(
    (it) =>
      !it.itemId &&
      it.rowKey != null &&
      String(it.rowKey).trim() !== '' &&
      String(it.rowKey) !== 'order-form-row-initial'
  );
  if (!drafts.length) return serverRows;
  const used = new Set(serverRows.map((s) => String(s.rowKey ?? s.id)));
  const extra = drafts.filter((d) => !used.has(String(d.rowKey ?? d.id)));
  return extra.length ? [...serverRows, ...extra] : serverRows;
}

function skuAutocompleteOptionLabel(option) {
  if (option == null || typeof option !== 'object') return '';
  const base = `${option.code || option.id} – ${option.productName} (${formatCurrency(option.price)})`;
  const attrs = option.attributes;
  if (attrs && typeof attrs === 'object' && Object.keys(attrs).length > 0) {
    const attrStr = Object.entries(attrs)
      .map(([k, v]) => `${k}: ${v}`)
      .join(' | ');
    return `${base} – ${attrStr}`;
  }
  return base;
}

function skuAutocompleteIsOptionEqual(opt, val) {
  if (val == null || val === '') return opt == null;
  if (typeof val === 'string') return typeof opt === 'object' && opt !== null && opt.id === val;
  if (typeof val === 'object' && val !== null) {
    return typeof opt === 'object' && opt !== null && opt.id === val.id;
  }
  return false;
}

function buildProductImageUrlById(productsList, allMedias, apiUrlBase) {
  const imagesMap = {};
  const base = apiUrlBase || process.env.REACT_APP_API_URL || 'http://localhost:8080';
  (productsList || []).forEach((product) => {
    const productMedias = (allMedias || [])
      .map((media) => {
        if (!media.metadata || media.metadata.length === 0) {
          return null;
        }
        const matchingMeta = media.metadata.find((meta) => meta.key === 'product_image');
        if (!matchingMeta) {
          return null;
        }
        const metaValue = matchingMeta.value;
        const isObject = typeof metaValue === 'object' && metaValue !== null;
        if (!isObject) {
          if (metaValue !== product.id) {
            return null;
          }
          return {
            ...media,
            order: 0,
          };
        }
        if (metaValue.id !== product.id) {
          return null;
        }
        return {
          ...media,
          order: metaValue?.order || 0,
        };
      })
      .filter((media) => media !== null)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    if (productMedias.length > 0) {
      imagesMap[product.id] = `${base}${productMedias[0].file_path}`;
    }
  });
  return imagesMap;
}

function normalizeForSearchToken(str) {
  if (str == null) return '';
  return String(str)
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

function buildOrderSkuSearchHaystack(sku) {
  const parts = [sku.code, sku.productCode, sku.productName, sku.productDescription];
  const attrs = sku.attributes;
  if (attrs && typeof attrs === 'object' && !Array.isArray(attrs)) {
    Object.keys(attrs).forEach((k) => {
      parts.push(k, attrs[k]);
    });
  }
  return normalizeForSearchToken(parts.filter((p) => p != null && String(p).trim() !== '').join(' '));
}

function filterOrderSkuAutocompleteOptions(options, params) {
  const raw = (params.inputValue || '').trim();
  if (!raw) {
    return options;
  }
  const tokens = raw
    .split(/\s+/)
    .map((t) => normalizeForSearchToken(t.trim()))
    .filter((t) => t.length > 0);
  if (tokens.length === 0) {
    return options;
  }
  return options.filter((opt) => {
    const hay = buildOrderSkuSearchHaystack(opt);
    return tokens.every((tok) => hay.includes(tok));
  });
}

/** Shared sx for all table-cell inputs: normalizes height to 30px across text, select, date variants */
const CF_INPUT_SX = {
  '& .MuiInputBase-root': { height: 30 },
  '& .MuiInputBase-input': { fontSize: '0.8125rem', padding: '4px 8px !important' },
  '& .MuiSelect-select.MuiSelect-outlined': { padding: '4px 8px !important', paddingRight: '24px !important' },
};

/** Autosave: selects/dates/discount save soon; long text (observations, title, status note) waits longer to avoid partial history. */
const ORDER_AUTOSAVE_QUICK_MS = 900;
const ORDER_AUTOSAVE_TEXT_MS = 2600;

const OrderFormPage = () => {
  const { translate } = useTranslation();
  const { currentWorkspace } = useWorkspace();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = !!id;
  const autoSaveQuickTimeoutRef = useRef(null);
  const autoSaveTextTimeoutRef = useRef(null);
  const pasteHandlerRef = useRef(null);
  const creatingRef = useRef(false);
  const formItemSaveTimeoutsRef = useRef({});
  const descriptionDraftByIdRef = useRef({});

  const [order, setOrder] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [skusByProduct, setSkusByProduct] = useState({});
  const [productImageUrlById, setProductImageUrlById] = useState({});
  const [formData, setFormData] = useState({
    customer_id: '',
    title: '',
    valid_until: '',
    delivery_date: '',
    status: 'DRAFT',
    observations: '',
    discount_value: null,
    created_at: '',
  });
  const [formItems, setFormItems] = useState([
    { id: 1, rowKey: 'order-form-row-initial', sku_id: '', quantity: 1, unit_price: 0, unit_priceDisplay: '', description: '' },
  ]);
  const formItemsRef = useRef([
    { id: 1, rowKey: 'order-form-row-initial', sku_id: '', quantity: 1, unit_price: 0, unit_priceDisplay: '', description: '' },
  ]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success', autoHideDuration: 6000 });
  const { busy: globalBusy, begin: beginGlobal, end: endGlobal } = useBusyCount();
  /** Auto-save do pedido (cliente, título, etc.): só indicação discreta, sem bloquear o formulário. */
  const [headerSaving, setHeaderSaving] = useState(false);
  const [savingItemId, setSavingItemId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [activeOrderTypes, setActiveOrderTypes] = useState([]);
  const [newOrderTypesLoaded, setNewOrderTypesLoaded] = useState(false);
  const [newOrderCreateError, setNewOrderCreateError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [importOrderDialogOpen, setImportOrderDialogOpen] = useState(false);
  const [importOrderTypeId, setImportOrderTypeId] = useState('');
  const [importSourceOrder, setImportSourceOrder] = useState(null);
  const [importIncLines, setImportIncLines] = useState(true);
  const [importIncHistory, setImportIncHistory] = useState(true);
  const [importIncOrderMedia, setImportIncOrderMedia] = useState(true);
  const [importIncHistoryMedia, setImportIncHistoryMedia] = useState(true);
  const [importIncObs, setImportIncObs] = useState(true);
  const [importIncDisc, setImportIncDisc] = useState(true);
  const [importIncTitle, setImportIncTitle] = useState(true);
  const [importIncCustom, setImportIncCustom] = useState(true);
  const [importSubmitting, setImportSubmitting] = useState(false);
  const [changeDialogTypes, setChangeDialogTypes] = useState([]);
  const [orderTypeChangeDialog, setOrderTypeChangeDialog] = useState(() => ({ ...initialOrderTypeChangeDialog }));
  const [isNewOrder, setIsNewOrder] = useState(false);
  const [customerInputText, setCustomerInputText] = useState('');
  const [confirmDelete, setConfirmDelete] = useState({ open: false, type: null, lineId: null, itemId: null });
  const [confirmHistoryBlockDelete, setConfirmHistoryBlockDelete] = useState({ open: false, historyId: null });
  const [confirmPaymentEntryDelete, setConfirmPaymentEntryDelete] = useState({ open: false, entryId: null });
  const [confirmSoftDeleteOrder, setConfirmSoftDeleteOrder] = useState(false);
  const [paymentEntryDialog, setPaymentEntryDialog] = useState({ open: false, mode: 'create', entry: null });
  const [discountPercentInput, setDiscountPercentInput] = useState('');
  const [discountValueInput, setDiscountValueInput] = useState('');
  const [finalValueInput, setFinalValueInput] = useState('');
  const [discountFieldFocused, setDiscountFieldFocused] = useState(null);
  const [mediaRefreshKey, setMediaRefreshKey] = useState(0);
  const [orderGalleryDialogOpen, setOrderGalleryDialogOpen] = useState(false);
  const [draftMediaRefreshKey, setDraftMediaRefreshKey] = useState(0);
  const [statusTransitionNote, setStatusTransitionNote] = useState('');
  const [statusFlowOpen, setStatusFlowOpen] = useState(false);
  const [statusFlowError, setStatusFlowError] = useState(null);
  const [savingHistory, setSavingHistory] = useState(false);
  /** Inline ghost row per line item: { name, value_type, value } — committed on blur when name is non-empty */
  const [draftCustomFields, setDraftCustomFields] = useState({});
  const draftCustomFieldsRef = useRef({});
  /** keys: `${formItemId}_${lid}` */
  const ghostCustomFieldCommitTimersRef = useRef({});
  /** lid of the draft whose name input should be focused on next render */
  const newDraftFocusLidRef = useRef(null);
  const itemCustomFieldMetaSaveTimeoutsRef = useRef({});
  const itemCustomizationSaveTimeoutsRef = useRef({});
  const itemCustomizationImageInputRef = useRef(null);
  const itemCustomizationImageTargetRef = useRef({ formItemId: null, key: null, formItem: null });
  const [customizationMediaPathById, setCustomizationMediaPathById] = useState({});
  const [customizationImageUploading, setCustomizationImageUploading] = useState(false);
  const [customizationImageLightbox, setCustomizationImageLightbox] = useState({
    open: false,
    items: [],
    active: null,
  });
  const savedToastDebounceRef = useRef(null);
  const orderRef = useRef(null);
  const pendingScrollToNewRowKeyRef = useRef(null);
  const nextLocalFormItemIdRef = useRef(10_000_000_000);
  /** After clear (X) or empty input, skip ensureCustomerOnBlur — blur fires before MUI applies clear. */
  const skipEnsureNextBlurRef = useRef(false);
  const formDataRef = useRef(formData);
  const statusTransitionNoteRef = useRef('');
  const persistOrderAfterEditRef = useRef(async () => {});
  /** Avoid autosave until GET /orders/:id has finished (prevents empty draft wiping server after new-order redirect). */
  const orderLoadHydratedRef = useRef(false);
  const routeOrderIdRef = useRef(id);
  const newOrderAutoKickoffRef = useRef(false);
  const newOrderResolvedTypeIdRef = useRef('');
  const createOrderAutomaticallyRef = useRef(async () => false);

  const { setPageTitle } = usePageTitle();
  const theme = useTheme();
  const {
    orderFormRowSx,
    orderItemDescFieldSx,
    orderItemGridSx,
    orderItemPriceFieldSx,
    orderItemQtyFieldSx,
    orderItemRowSx,
    orderItemSkuFieldSx,
    orderItemsToolbarSx,
    orderLayoutGridSx,
    orderMainColumnSx,
    orderMockCardPadSx,
    orderMockCardSx,
    orderPageWrapSx,
    orderRailHeadingSx,
    orderRailObsGridSx,
    orderRailSummaryGridSx,
    orderSectionTitleSx,
    orderSideRailSx,
    orderStackBelowSx,
    orderSummaryLinesSx,
    orderSummaryTotalSx,
    orderTimelineHeaderSx,
    orderTimelineIngestOuterSx,
    orderTimelineSectionSx,
    orderTimelineTitleSx,
    orderStatusTransitionCardSx,
    orderStatusTransitionHeaderRowSx,
    orderStatusTransitionTitleSx,
    orderStatusTransitionDraftChipSx,
    orderStatusTransitionHintSx,
  } = useMemo(() => getOrderThemeLayoutSx(theme), [theme]);

  useEffect(() => {
    const prefix = order?.number ? `#${order.number} – ` : '';
    const label = isNewOrder ? translate('orders.create') : translate('orders.edit');
    setPageTitle(`${prefix}${label}`);
    return () => setPageTitle(null);
  }, [order?.number, isNewOrder, translate, setPageTitle]);

  useEffect(() => {
    orderRef.current = order;
  }, [order]);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  useEffect(() => {
    statusTransitionNoteRef.current = statusTransitionNote;
  }, [statusTransitionNote]);

  useEffect(() => {
    routeOrderIdRef.current = id;
  }, [id]);

  useEffect(() => {
    draftCustomFieldsRef.current = draftCustomFields;
  }, [draftCustomFields]);

  const queueSavedToast = useCallback(() => {
    if (savedToastDebounceRef.current) clearTimeout(savedToastDebounceRef.current);
    savedToastDebounceRef.current = setTimeout(() => {
      setSnackbar({
        open: true,
        message: translate('common.saved'),
        severity: 'success',
        autoHideDuration: 2200,
      });
      savedToastDebounceRef.current = null;
    }, 450);
  }, [translate]);

  useEffect(() => {
    return () => {
      if (savedToastDebounceRef.current) {
        clearTimeout(savedToastDebounceRef.current);
        savedToastDebounceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    loadCustomers();
    if (!id && !creatingRef.current && !order) {
      setIsNewOrder(true);
      (async () => {
        try {
          const tr = await listOrderTypes(true);
          setActiveOrderTypes(tr.data || []);
        } catch (_) {
          setActiveOrderTypes([]);
        } finally {
          setNewOrderTypesLoaded(true);
        }
        await loadDefaultValidUntil();
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!orderTypeChangeDialog.open || orderTypeChangeDialog.step !== 1) return;
    (async () => {
      try {
        const tr = await listOrderTypes(true);
        setChangeDialogTypes(tr.data || []);
      } catch (_) {
        setChangeDialogTypes([]);
      }
    })();
  }, [orderTypeChangeDialog.open, orderTypeChangeDialog.step]);

  useEffect(() => {
    if (currentWorkspace) {
      loadProducts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.id]);

  useEffect(() => {
    if (id) {
      const isNewlyCreated = sessionStorage.getItem(`newOrder_${id}`);
      if (isNewlyCreated === 'true') {
        setIsNewOrder(true);
        sessionStorage.removeItem(`newOrder_${id}`);
      } else {
        setIsNewOrder(false);
      }
      loadOrder();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const uploadDraftMediaFile = useCallback(async (file) => {
    const oid = orderRef.current?.id;
    if (!oid || !file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('metadata_key', 'order_status_draft');
    fd.append(
      'metadata_json',
      JSON.stringify({
        id: oid,
        url: '',
        title: file.name,
        order: 0,
      })
    );
    await uploadMedia(fd);
    setDraftMediaRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    const handlePaste = (e) => {
      if (!order?.id) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            uploadDraftMediaFile(file).catch((err) => {
              console.error(err);
            });
          }
        }
      }
    };

    if (order?.id) {
      document.addEventListener('paste', handlePaste);
      pasteHandlerRef.current = handlePaste;
    }

    return () => {
      if (pasteHandlerRef.current) {
        document.removeEventListener('paste', pasteHandlerRef.current);
      }
    };
  }, [order?.id, uploadDraftMediaFile]);

  const createOrderAutomatically = async (validUntil = null, orderTypeId = '') => {
    if (creatingRef.current || order) return false;
    const tid = String(orderTypeId || '').trim();
    if (!tid) {
      setSnackbar({ open: true, message: translate('orders.order_type_required'), severity: 'error' });
      return false;
    }
    creatingRef.current = true;
    setCreating(true);
    try {
      let currentValidUntil = validUntil || (formData.valid_until ? displayBRToISO(formData.valid_until) : null) || null;
      if (currentValidUntil === '') {
        currentValidUntil = null;
      }
      const settingsRes = await getSettings();
      const bd = settingsRes.data?.order_default_validity_days ?? 7;
      const base = startOfLocalDay(new Date());
      let deliveryISO =
        bd > 0 ? formatDateISO(addBusinessDays(base, bd)) : currentValidUntil || formatDateToISO(base);
      const res = await createOrder({
        customer_id: '',
        title: '',
        order_type_id: tid,
        valid_until: currentValidUntil,
        delivery_date: deliveryISO,
        created_by: '',
        lines: [],
      });
      setLastUsedOrderTypeId(currentWorkspace?.id, tid);
      setOrder(res.data);
      if (res.data.valid_until) {
        const validUntilDisplay = isoToDisplayBR(res.data.valid_until);
        setFormData((f) => ({
          ...f,
          valid_until: validUntilDisplay,
          delivery_date: f.delivery_date || validUntilDisplay || (res.data.delivery_date ? isoToDisplayBR(res.data.delivery_date) : ''),
        }));
      } else if (validUntil) {
        const vu = isoToDisplayBR(validUntil);
        setFormData((f) => ({ ...f, valid_until: vu, delivery_date: f.delivery_date || vu }));
      } else if (res.data.delivery_date) {
        setFormData((f) => ({ ...f, delivery_date: isoToDisplayBR(res.data.delivery_date) }));
      }
      
      if (res.data && (!res.data.lines || res.data.lines.length === 0)) {
        await addOrderLine(res.data.id, { sequence: 1, description: '' });
        const updatedRes = await getOrder(res.data.id);
        setOrder(updatedRes.data);
        
        const defaultItem = [
          {
            id: 1,
            rowKey: 'order-form-row-initial',
            itemId: null,
            lineId: null,
            sku_id: '',
            quantity: 1,
            unit_price: 0,
            unit_priceDisplay: '',
            description: '',
            customization_fields: [],
            customizationValues: {},
            selectOtherActive: {},
          },
        ];
        setFormItems(defaultItem);
        formItemsRef.current = defaultItem;
      } else {
        const savedItems = orderLinesBySequence(res.data?.lines).flatMap((line) =>
          (line.items || []).map((item) => {
            const displayPrice = item.unit_price > 0 ? formatCurrency(item.unit_price) : '';
            return {
              id: `saved_${item.id}`,
              rowKey: item.id,
              itemId: item.id,
              lineId: line.id,
              sku_id: item.sku_id,
              original_sku_id: item.sku_id ?? '',
              quantity: item.quantity,
              unit_price: item.unit_price,
              unit_priceDisplay: displayPrice,
              description: item.description || '',
            };
          })
        );
        
        if (savedItems.length > 0) {
          setFormItems(savedItems);
          formItemsRef.current = savedItems;
        } else {
          const defaultItem = [
            {
              id: 1,
              rowKey: 'order-form-row-initial',
              itemId: null,
              lineId: null,
              sku_id: '',
              quantity: 1,
              unit_price: 0,
              unit_priceDisplay: '',
              description: '',
              customization_fields: [],
              customizationValues: {},
              selectOtherActive: {},
            },
          ];
          setFormItems(defaultItem);
          formItemsRef.current = defaultItem;
        }
      }
      
      sessionStorage.setItem(`newOrder_${res.data.id}`, 'true');
      navigate(`/orders/${res.data.id}/edit`, { replace: true });
      return true;
    } catch (err) {
      if (!id) {
        const msg = err.message && !err.message.includes('already exists') ? err.message : translate('orders.new_order_create_failed');
        setNewOrderCreateError(msg);
      } else if (err.message && !err.message.includes('already exists')) {
        setSnackbar({ open: true, message: err.message, severity: 'error' });
      }
      return false;
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  };

  createOrderAutomaticallyRef.current = createOrderAutomatically;

  const handleCloseImportDialog = useCallback(() => {
    setImportOrderDialogOpen(false);
    setImportSourceOrder(null);
    if (!id) {
      const tid = String(importOrderTypeId || newOrderResolvedTypeIdRef.current || '').trim();
      if (tid) {
        newOrderResolvedTypeIdRef.current = tid;
      }
      void createOrderAutomaticallyRef.current(null, newOrderResolvedTypeIdRef.current);
    }
  }, [id, importOrderTypeId]);

  const submitImportOrder = async () => {
    if (!importSourceOrder?.id) {
      setSnackbar({ open: true, message: translate('orders.import_source_required'), severity: 'error' });
      return;
    }
    if (id && String(importSourceOrder.id) === String(id)) {
      setSnackbar({ open: true, message: translate('orders.import_same_as_current'), severity: 'error' });
      return;
    }
    const tid = String(importOrderTypeId || newOrderResolvedTypeIdRef.current || '').trim();
    if (!id && !tid) {
      setSnackbar({ open: true, message: translate('orders.order_type_required'), severity: 'error' });
      return;
    }
    setImportSubmitting(true);
    try {
      const baseBody = {
        source_order_id: importSourceOrder.id,
        mode: 'selective',
        include_lines: importIncLines,
        include_status_history: importIncHistory,
        include_order_level_media: importIncOrderMedia,
        include_history_media: importIncHistoryMedia,
        include_observations: importIncObs,
        include_discount: importIncDisc,
        include_title: importIncTitle,
        include_customizations: importIncCustom,
      };
      if (id) {
        await importOrderDataInto(id, baseBody);
        setImportOrderDialogOpen(false);
        setImportSourceOrder(null);
        setSnackbar({ open: true, message: translate('orders.import_applied'), severity: 'success', autoHideDuration: 6000 });
        setMediaRefreshKey((k) => k + 1);
        await loadOrder();
        return;
      }
      let currentValidUntil = formData.valid_until ? displayBRToISO(formData.valid_until) : null;
      if (currentValidUntil === '') {
        currentValidUntil = null;
      }
      const settingsRes = await getSettings();
      const bd = settingsRes.data?.order_default_validity_days ?? 7;
      const base = startOfLocalDay(new Date());
      const deliveryISO =
        bd > 0 ? formatDateISO(addBusinessDays(base, bd)) : currentValidUntil || formatDateToISO(base);
      const body = {
        ...baseBody,
        order_type_id: tid,
        customer_id: '',
        valid_until: currentValidUntil,
        delivery_date: deliveryISO,
        created_by: '',
      };
      const res = await createOrderFromSource(body);
      setImportOrderDialogOpen(false);
      sessionStorage.setItem(`newOrder_${res.data.id}`, 'true');
      navigate(`/orders/${res.data.id}/edit`, { replace: true });
    } catch (err) {
      const msg =
        err.message && String(err.message).trim() ? err.message : translate('orders.new_order_create_failed');
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setImportSubmitting(false);
    }
  };

  useEffect(() => {
    if (id || order) return;
    if (!newOrderTypesLoaded || activeOrderTypes.length === 0) return;
    if (newOrderAutoKickoffRef.current) return;
    const wid = currentWorkspace?.id;
    const preferred = location.state?.preferredOrderTypeId;
    const last = getLastUsedOrderTypeId(wid);
    const tid = pickActiveOrderTypeId(activeOrderTypes, preferred, last);
    newOrderResolvedTypeIdRef.current = tid;
    setNewOrderCreateError(null);
    const wantsImport = searchParams.get('import') === '1';
    if (wantsImport) {
      newOrderAutoKickoffRef.current = true;
      setImportOrderTypeId(tid);
      setImportOrderDialogOpen(true);
      setSearchParams({}, { replace: true });
      return;
    }
    newOrderAutoKickoffRef.current = true;
    void createOrderAutomaticallyRef.current(null, tid);
  }, [id, order, newOrderTypesLoaded, activeOrderTypes, location.state, currentWorkspace?.id, searchParams, setSearchParams]);

  useEffect(() => {
    if (!importIncHistory && importIncHistoryMedia) {
      setImportIncHistoryMedia(false);
    }
  }, [importIncHistory, importIncHistoryMedia]);

  const loadCustomers = async () => {
    try {
      const res = await getCustomers();
      setCustomers(res.data?.customers || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadDefaultValidUntil = async () => {
    try {
      const res = await getSettings();
      const days = res.data?.order_default_validity_days ?? 7;
      if (days > 0) {
        const base = startOfLocalDay(new Date());
        const validDate = addBusinessDays(base, days);
        const validUntilISO = formatDateISO(validDate);
        const validUntilDisplay = isoToDisplayBR(validUntilISO);
        setFormData((f) => ({ ...f, valid_until: validUntilDisplay, delivery_date: validUntilDisplay }));
        return validUntilISO;
      }
      return '';
    } catch (e) {
      console.error(e);
      return '';
    }
  };

  const loadProducts = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const [res, mediasRes] = await Promise.all([getProducts(), getMedias()]);
      const productsList = res.data?.products || [];
      setProducts(productsList);
      const allMedias = mediasRes.data?.medias || [];
      setProductImageUrlById(buildProductImageUrlById(productsList, allMedias, apiUrl));
      const skuPairs = await Promise.all(
        productsList.map(async (p) => {
          try {
            const skuRes = await getSKUs(p.id);
            return [p.id, skuRes.data ?? []];
          } catch (_) {
            return [p.id, []];
          }
        })
      );
      const nextSkus = {};
      skuPairs.forEach(([pid, skus]) => {
        nextSkus[pid] = skus;
      });
      setSkusByProduct(nextSkus);
    } catch (e) {
      console.error(e);
    }
  };

  const loadOrder = async () => {
    orderLoadHydratedRef.current = false;
    try {
      const res = await getOrder(id);
      if (!res.data) {
        setSnackbar({ open: true, message: 'Pedido não encontrado', severity: 'error' });
        return;
      }
      
      setOrder(res.data);
      const draftFromServer = res.data?.status_transition_draft_note || '';
      setStatusTransitionNote(draftFromServer);
      statusTransitionNoteRef.current = draftFromServer;
      const customerId = res.data?.customer_id || '';
      const customerName = res.data?.customer_name || '';
      let settingsDays = 7;
      try {
        const sr = await getSettings();
        settingsDays = sr.data?.order_default_validity_days ?? 7;
      } catch (_) {
        settingsDays = 7;
      }
      let deliveryDisplay = '';
      if (res.data?.delivery_date) {
        deliveryDisplay = isoToDisplayBR(res.data.delivery_date);
      } else if (settingsDays > 0 && res.data?.created_at) {
        const delIso = expectedDeliveryISOFromCreatedAt(res.data.created_at, settingsDays);
        if (delIso) deliveryDisplay = isoToDisplayBR(delIso);
      }
      if (!deliveryDisplay && res.data?.valid_until) {
        deliveryDisplay = isoToDisplayBR(res.data.valid_until);
      }
      setFormData({
        customer_id: customerId,
        title: res.data?.title || '',
        valid_until: res.data?.valid_until ? isoToDisplayBR(res.data.valid_until) : '',
        delivery_date: deliveryDisplay,
        status: res.data?.status || 'DRAFT',
        observations: res.data?.observations || '',
        discount_value: res.data?.discount_value ?? null,
        created_at: res.data?.created_at ? isoToDisplayBR(res.data.created_at) : '',
      });
      if (customerId && customerName) {
        setCustomerInputText(customerName);
      } else {
        setCustomerInputText('');
      }
      
      let orderData = res.data;
      if (orderData && (!orderData.lines || orderData.lines.length === 0)) {
        await addOrderLine(orderData.id, { sequence: 1, description: '' });
        const updatedRes = await getOrder(id);
        orderData = updatedRes.data;
        setOrder(orderData);
        const draftAfterLine = orderData?.status_transition_draft_note || '';
        setStatusTransitionNote(draftAfterLine);
        statusTransitionNoteRef.current = draftAfterLine;
      }
      
      const savedItems = orderLinesBySequence(orderData?.lines).flatMap((line) =>
        (line.items || []).map((item) => {
          const displayPrice = item.unit_price > 0 ? formatCurrency(item.unit_price) : '';
          const itemCustomizationFields = item.customization_fields || [];
          const itemCustomizationValues = Object.fromEntries(
            itemCustomizationFields.map((f) => [f.key, f.value || ''])
          );
          const itemSelectOtherActive = {};
          itemCustomizationFields.forEach((f) => {
            if (f.value_type === 'select' && f.allow_other && f.value && f.options && !f.options.includes(f.value)) {
              itemSelectOtherActive[f.key] = true;
            }
          });
          return {
            id: `saved_${item.id}`,
            rowKey: item.id,
            itemId: item.id,
            lineId: line.id,
            sku_id: item.sku_id,
            original_sku_id: item.sku_id ?? '',
            quantity: item.quantity,
            unit_price: item.unit_price,
            unit_priceDisplay: displayPrice,
            description: item.description || '',
            customization_fields: itemCustomizationFields,
            customizationValues: itemCustomizationValues,
            selectOtherActive: itemSelectOtherActive,
          };
        })
      );
      
      if (savedItems.length > 0) {
        const merged = appendUnsavedExtraFormItemRows(savedItems, formItemsRef.current);
        setFormItems(merged);
        formItemsRef.current = merged;
      } else {
        const defaultItem = [
          {
            id: 1,
            rowKey: 'order-form-row-initial',
            itemId: null,
            lineId: null,
            sku_id: '',
            quantity: 1,
            unit_price: 0,
            unit_priceDisplay: '',
            description: '',
            customization_fields: [],
            customizationValues: {},
            selectOtherActive: {},
          },
        ];
        const merged = appendUnsavedExtraFormItemRows(defaultItem, formItemsRef.current);
        setFormItems(merged);
        formItemsRef.current = merged;
      }

      if (!orderData?.order_type_id) {
        setOrderTypeChangeDialog({
          open: true,
          step: 1,
          targetTypeId: '',
          statusMap: {},
          targetStatuses: [],
          reason: 'assign',
        });
      } else {
        setOrderTypeChangeDialog((prev) => (prev.reason === 'assign' ? { ...initialOrderTypeChangeDialog } : prev));
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      orderLoadHydratedRef.current = true;
    }
  };

  const typesForOrderTypeChangeDialog = useMemo(() => {
    const cur = order?.order_type_id;
    if (!cur) return changeDialogTypes;
    return changeDialogTypes.filter((t) => t.id !== cur);
  }, [changeDialogTypes, order?.order_type_id]);

  const advanceOrderTypeDialogToStep2 = async () => {
    const tid = String(orderTypeChangeDialog.targetTypeId || '').trim();
    if (!tid) {
      setSnackbar({ open: true, message: translate('orders.order_type_required'), severity: 'error' });
      return;
    }
    const oid = order?.id;
    if (!oid) return;
    try {
      const res = await getOrderTypeStatuses(tid);
      const inner = res && res.data;
      const payload = inner || {};
      const targetStatuses = Array.isArray(payload) ? payload : payload.statuses || [];
      const sourceIds = collectDistinctStatusIdsFromOrder(order);
      const targetIdSet = new Set(targetStatuses.map((r) => r.status_definition_id));
      const statusMap = {};
      sourceIds.forEach((sid) => {
        if (targetIdSet.has(sid)) statusMap[sid] = sid;
      });
      setOrderTypeChangeDialog((d) => ({
        ...d,
        step: 2,
        targetStatuses,
        statusMap: { ...statusMap },
      }));
    } catch (e) {
      setSnackbar({ open: true, message: e.message || translate('common.error'), severity: 'error' });
    }
  };

  const submitOrderTypeChange = async () => {
    const d = orderTypeChangeDialog;
    const oid = order?.id;
    if (!oid || !d.targetTypeId) return;
    const sourceIds = collectDistinctStatusIdsFromOrder(order);
    for (let i = 0; i < sourceIds.length; i += 1) {
      const sid = sourceIds[i];
      if (!d.statusMap[sid]) {
        setSnackbar({ open: true, message: translate('orders.change_type_map_incomplete'), severity: 'error' });
        return;
      }
    }
    try {
      await changeOrderType(oid, {
        order_type_id: d.targetTypeId,
        status_map: d.statusMap,
      });
      setOrderTypeChangeDialog({ ...initialOrderTypeChangeDialog });
      await loadOrder();
      setSnackbar({ open: true, message: translate('common.saved'), severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: e.message || translate('common.error'), severity: 'error' });
    }
  };

  const handleOpenStatusFlow = useCallback(() => {
    if (!order?.order_type_id) return;
    setStatusFlowOpen(true);
    setStatusFlowError(null);
  }, [order?.order_type_id]);

  const persistOrderAfterEdit = async () => {
    const o = orderRef.current;
    if (!o?.id) return;
    if (!o.order_type_id) return;
    const routeId = routeOrderIdRef.current;
    if (routeId && o.id !== routeId) return;
    setHeaderSaving(true);
    try {
      const fd = formDataRef.current;
      const note = statusTransitionNoteRef.current;
      const payload = buildOrderUpdatePayloadFromFormData(fd, orderRef.current, note);
      if (!payload.delivery_date) {
        setSnackbar({ open: true, message: translate('orders.delivery_date_required'), severity: 'error', autoHideDuration: 5000 });
        setHeaderSaving(false);
        return;
      }
      const res = await updateOrder(o.id, payload);
      const hid = res.data?.status_history_id;
      if (hid) {
        setHeaderSaving(false);
        try {
          await migrateOrderDraftMediaToHistory(o.id, hid);
          setDraftMediaRefreshKey((k) => k + 1);
          setMediaRefreshKey((k) => k + 1);
          setStatusTransitionNote('');
          statusTransitionNoteRef.current = '';
          await loadOrder();
        } catch (migrateErr) {
          setSnackbar({ open: true, message: migrateErr?.message || String(migrateErr), severity: 'error', autoHideDuration: 6000 });
        }
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error', autoHideDuration: 6000 });
    } finally {
      setHeaderSaving(false);
    }
  };

  persistOrderAfterEditRef.current = persistOrderAfterEdit;

  const handleSaveHistoryNow = async () => {
    const o = orderRef.current;
    if (!o?.id) return;
    const routeId = routeOrderIdRef.current;
    if (routeId && o.id !== routeId) return;
    if (autoSaveQuickTimeoutRef.current) {
      clearTimeout(autoSaveQuickTimeoutRef.current);
      autoSaveQuickTimeoutRef.current = null;
    }
    if (autoSaveTextTimeoutRef.current) {
      clearTimeout(autoSaveTextTimeoutRef.current);
      autoSaveTextTimeoutRef.current = null;
    }
    setSavingHistory(true);
    try {
      const fd = formDataRef.current;
      const note = statusTransitionNoteRef.current;
      const payload = buildOrderUpdatePayloadFromFormData(fd, orderRef.current, note, { force_status_history_entry: true });
      if (!payload.delivery_date) {
        setSnackbar({ open: true, message: translate('orders.delivery_date_required'), severity: 'error', autoHideDuration: 5000 });
        setSavingHistory(false);
        return;
      }
      const res = await updateOrder(o.id, payload);
      const hid = res.data?.status_history_id;
      if (hid) {
        beginGlobal();
        try {
          await migrateOrderDraftMediaToHistory(o.id, hid);
          setDraftMediaRefreshKey((k) => k + 1);
          setMediaRefreshKey((k) => k + 1);
          setStatusTransitionNote('');
          statusTransitionNoteRef.current = '';
          await loadOrder();
        } finally {
          endGlobal();
        }
        setSnackbar({
          open: true,
          message: translate('orders.save_history_success'),
          severity: 'success',
          autoHideDuration: 2200,
        });
      } else {
        setSnackbar({
          open: true,
          message: translate('orders.save_history_unexpected'),
          severity: 'error',
          autoHideDuration: 4000,
        });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error', autoHideDuration: 6000 });
    } finally {
      setSavingHistory(false);
    }
  };

  const flushPendingOrderSave = useCallback(() => {
    if (autoSaveQuickTimeoutRef.current) {
      clearTimeout(autoSaveQuickTimeoutRef.current);
      autoSaveQuickTimeoutRef.current = null;
    }
    if (autoSaveTextTimeoutRef.current) {
      clearTimeout(autoSaveTextTimeoutRef.current);
      autoSaveTextTimeoutRef.current = null;
    }
    void persistOrderAfterEditRef.current();
  }, []);

  const scheduleQuickOrderSave = useCallback(() => {
    if (!order?.id) return;
    if (!orderLoadHydratedRef.current) return;
    if (id && order.id !== id) return;
    void formData.status;
    void formData.valid_until;
    void formData.delivery_date;
    void formData.customer_id;
    void formData.discount_value;
    void formData.created_at;

    if (autoSaveQuickTimeoutRef.current) {
      clearTimeout(autoSaveQuickTimeoutRef.current);
    }

    autoSaveQuickTimeoutRef.current = setTimeout(async () => {
      try {
        await persistOrderAfterEditRef.current();
      } catch (err) {
        setSnackbar({ open: true, message: err?.message || String(err), severity: 'error', autoHideDuration: 6000 });
      }
    }, ORDER_AUTOSAVE_QUICK_MS);
  }, [id, order?.id, formData.status, formData.valid_until, formData.delivery_date, formData.customer_id, formData.discount_value, formData.created_at]);

  const scheduleTextOrderSave = useCallback(() => {
    if (!order?.id) return;
    if (!orderLoadHydratedRef.current) return;
    if (id && order.id !== id) return;
    void formData.observations;
    void formData.title;
    void statusTransitionNote;

    if (autoSaveTextTimeoutRef.current) {
      clearTimeout(autoSaveTextTimeoutRef.current);
    }

    autoSaveTextTimeoutRef.current = setTimeout(async () => {
      try {
        await persistOrderAfterEditRef.current();
      } catch (err) {
        setSnackbar({ open: true, message: err?.message || String(err), severity: 'error', autoHideDuration: 6000 });
      }
    }, ORDER_AUTOSAVE_TEXT_MS);
  }, [id, order?.id, formData.observations, formData.title, statusTransitionNote]);

  useEffect(() => {
    if (order?.id) {
      scheduleQuickOrderSave();
    }
    return () => {
      if (autoSaveQuickTimeoutRef.current) {
        clearTimeout(autoSaveQuickTimeoutRef.current);
        autoSaveQuickTimeoutRef.current = null;
      }
    };
  }, [scheduleQuickOrderSave, order?.id]);

  useEffect(() => {
    if (order?.id) {
      scheduleTextOrderSave();
    }
    return () => {
      if (autoSaveTextTimeoutRef.current) {
        clearTimeout(autoSaveTextTimeoutRef.current);
        autoSaveTextTimeoutRef.current = null;
      }
    };
  }, [scheduleTextOrderSave, order?.id]);

  useEffect(() => {
    const onPageHide = () => {
      flushPendingOrderSave();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        flushPendingOrderSave();
      }
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [flushPendingOrderSave]);

  useEffect(() => {
    return () => {
      flushPendingOrderSave();
    };
  }, [flushPendingOrderSave]);

  const handleCustomerChange = async (_, option) => {
    if (option == null) {
      skipEnsureNextBlurRef.current = true;
      setCustomerInputText('');
      setFormData((f) => ({ ...f, customer_id: '' }));
      return;
    }
    let customerId = '';
    if (typeof option === 'object') {
      customerId = option.id || '';
      setCustomerInputText(getCustomerOptionLabel(option));
    } else if (typeof option === 'string' && option.trim()) {
      try {
        const customerRes = await createCustomer({
          name: option.trim(),
          email: '',
          phone: '',
          document: '',
          address: '',
        });
        customerId = customerRes.data?.id || '';
        await loadCustomers();
        setCustomerInputText(option.trim());
      } catch (err) {
        setSnackbar({ open: true, message: err.message, severity: 'error' });
        return;
      }
    } else {
      setCustomerInputText('');
    }

    setFormData((f) => ({ ...f, customer_id: customerId }));
  };

  const ensureCustomerOnBlur = useCallback(async () => {
    const input = (customerInputText || '').trim();
    if (!input) {
      setFormData((f) => ({ ...f, customer_id: '' }));
      return;
    }
    const fd = formDataRef.current;
    if (fd.customer_id) {
      const sel = customers.find((c) => c.id === fd.customer_id);
      if (sel) {
        if (
          getCustomerOptionLabel(sel) === customerInputText ||
          sel.name.trim().toLowerCase() === input.toLowerCase()
        ) {
          return;
        }
      } else {
        return;
      }
    }
    const existing = customers.find(
      (c) => c.name.trim().toLowerCase() === input.toLowerCase()
    );
    if (existing) {
      setFormData((f) => ({ ...f, customer_id: existing.id }));
      setCustomerInputText(getCustomerOptionLabel(existing));
      return;
    }
    try {
      const customerRes = await createCustomer({
        name: input,
        email: '',
        phone: '',
        document: '',
        address: '',
      });
      const newId = customerRes.data?.id;
      if (!newId) return;
      await loadCustomers();
      setFormData((f) => ({ ...f, customer_id: newId }));
      setCustomerInputText(input);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  }, [customerInputText, customers]);

  const handleAddNewFormLine = () => {
    setFormItems(prevItems => {
      const newId = nextLocalFormItemIdRef.current;
      nextLocalFormItemIdRef.current += 1;
      const rowKey = newOrderFormItemRowKey();
      pendingScrollToNewRowKeyRef.current = rowKey;
      const newItems = [...prevItems, { id: newId, rowKey, itemId: null, lineId: null, sku_id: '', quantity: 1, unit_price: 0, unit_priceDisplay: '', description: '', customization_fields: [], customizationValues: {}, selectOtherActive: {} }];
      formItemsRef.current = newItems;
      return newItems;
    });
  };

  useLayoutEffect(() => {
    const rk = pendingScrollToNewRowKeyRef.current;
    if (rk == null || rk === '') return;
    pendingScrollToNewRowKeyRef.current = null;
    const esc = typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(String(rk)) : String(rk);
    const el = document.querySelector(`[data-order-form-item="${esc}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    requestAnimationFrame(() => {
      const wrap = document.querySelector(`[data-order-form-item="${esc}"]`);
      const inp =
        wrap?.querySelector?.('.order-item-desc textarea') ||
        wrap?.querySelector?.('.order-item-desc input') ||
        wrap?.querySelector?.('input');
      if (inp && typeof inp.focus === 'function') {
        inp.focus();
      }
    });
  }, [formItems]);

  const handleSaveFormItem = async (formItemId) => {
    if (!order?.id) return;

    if (formItemSaveTimeoutsRef.current[formItemId]) {
      clearTimeout(formItemSaveTimeoutsRef.current[formItemId]);
      delete formItemSaveTimeoutsRef.current[formItemId];
    }

    const formItem = formItemsRef.current.find((item) => item.id === formItemId);
    const hasProduct = formItem?.sku_id;
    const hasFreeItemDescription = formItem?.description?.trim();
    const canSave = formItem && formItem.quantity >= 1 && formItem.unit_price >= 0 && (hasProduct || hasFreeItemDescription);
    if (!canSave) return;

    try {
      if (formItem.itemId && formItem.lineId) {
        const normOrderSku = (v) => (v == null || v === '' ? '' : String(v));
        const skuChanged =
          normOrderSku(formItem.sku_id) !== normOrderSku(formItem.original_sku_id);
        if (skuChanged) {
          try {
            const customizationValuesSnapshot = { ...(formItem.customizationValues || {}) };
            const preservedFieldPayloads = (formItem.customization_fields || []).map((f) => ({
              key: f.key,
              name: f.name,
              value_type: f.value_type || 'text',
              value: customizationValuesSnapshot[f.key] ?? f.value ?? '',
              hide_in_public_form: !!f.hide_in_public_form,
            }));

            await deleteOrderLineItem(order.id, formItem.lineId, formItem.itemId);
            const newItemRes = await addOrderLineItem(order.id, formItem.lineId, {
              sku_id: formItem.sku_id || '',
              quantity: formItem.quantity,
              unit_price: formItem.unit_price || 0,
              description: formItem.description || '',
            });
            const newItemId = newItemRes.data?.id;
            let orderAfter = await getOrder(order.id);
            let newItemFromOrder = orderLinesBySequence(orderAfter.data?.lines).flatMap((l) => l.items || []).find((i) => i.id === newItemId);

            if (newItemId) {
              const preservedKeys = new Set(preservedFieldPayloads.map((p) => p.key));
              const serverFields = newItemFromOrder?.customization_fields || [];
              const serverOnlyPayloads = serverFields
                .filter((f) => !preservedKeys.has(f.key))
                .map((f) => ({
                  key: f.key,
                  name: f.name,
                  value_type: f.value_type || 'text',
                  value: f.value || '',
                  hide_in_public_form: !!f.hide_in_public_form,
                }));
              const mergedPayload = [...preservedFieldPayloads, ...serverOnlyPayloads];
              if (mergedPayload.length > 0) {
                await upsertOrderLineItemCustomizationFields(order.id, formItem.lineId, newItemId, mergedPayload);
                orderAfter = await getOrder(order.id);
                newItemFromOrder = orderLinesBySequence(orderAfter.data?.lines).flatMap((l) => l.items || []).find((i) => i.id === newItemId);
              }
            }

            setOrder(orderAfter.data);
            const itemCustomizationFields = newItemFromOrder?.customization_fields || [];
            const itemCustomizationValues = Object.fromEntries(itemCustomizationFields.map((f) => [f.key, f.value || '']));
            const itemSelectOtherActive = {};
            itemCustomizationFields.forEach((f) => {
              if (f.value_type === 'select' && f.allow_other && f.value && f.options && !f.options.includes(f.value)) {
                itemSelectOtherActive[f.key] = true;
              }
            });
            setFormItems((prevItems) => {
              const currentItem = prevItems.find((item) => item.id === formItemId);
              const updated = prevItems.map((item) =>
                item.id === formItemId
                  ? {
                      ...item,
                      rowKey: newItemId || item.rowKey,
                      itemId: newItemId || null,
                      lineId: formItem.lineId,
                      sku_id: formItem.sku_id,
                      original_sku_id: formItem.sku_id,
                      unit_priceDisplay: currentItem?.unit_priceDisplay || (formItem.unit_price > 0 ? formatCurrency(formItem.unit_price) : ''),
                      customization_fields: itemCustomizationFields,
                      customizationValues: itemCustomizationValues,
                      selectOtherActive: itemSelectOtherActive,
                    }
                  : item
              );
              formItemsRef.current = updated;
              return updated;
            });
            queueSavedToast();
          } catch (err) {
            setSnackbar({ open: true, message: err.message, severity: 'error', autoHideDuration: 6000 });
          }
          return;
        }
        const unitPrice = formItem.unit_price || 0;
      try {
        await updateOrderLineItem(order.id, formItem.lineId, formItem.itemId, {
          quantity: formItem.quantity,
          unit_price: unitPrice,
          description: formItem.description || '',
        });

        setFormItems((prevItems) => {
            const currentItem = prevItems.find((item) => item.id === formItemId);
            const preserveDisplay = currentItem?.unit_priceDisplay && currentItem.unit_priceDisplay.trim() !== '' && currentItem.unit_price > 0;
            const updated = prevItems.map((item) =>
              item.id === formItemId
                ? {
                    ...item,
                    unit_price: unitPrice,
                    unit_priceDisplay: preserveDisplay ? currentItem.unit_priceDisplay : unitPrice > 0 ? formatCurrency(unitPrice) : '',
                  }
                : item
            );
            formItemsRef.current = updated;
            return updated;
          });
          queueSavedToast();
        } catch (err) {
          setSnackbar({ open: true, message: err.message, severity: 'error', autoHideDuration: 6000 });
        }
        return;
      }

      const unitPrice = formItem.unit_price || 0;

      let targetLineId = null;
      if (!order?.lines || order.lines.length === 0) {
        await addOrderLine(order.id, { sequence: 1, description: '' });
        const updatedOrder = await getOrder(order.id);
        setOrder(updatedOrder.data);
        const linesOrdered = orderLinesBySequence(updatedOrder.data?.lines);
        targetLineId = linesOrdered[0]?.id ?? null;
      } else {
        targetLineId = orderLinesBySequence(order.lines)[0]?.id;
      }

      if (targetLineId) {
        const newItemRes = await addOrderLineItem(order.id, targetLineId, {
          sku_id: formItem.sku_id || '',
          quantity: formItem.quantity,
          unit_price: unitPrice,
          description: formItem.description || '',
        });

        const updatedOrder = await getOrder(order.id);
        setOrder(updatedOrder.data);
        const newItemFromOrder = orderLinesBySequence(updatedOrder.data?.lines).flatMap((l) => l.items || []).find((i) => i.id === newItemRes.data?.id);
        const itemCustomizationFields = newItemFromOrder?.customization_fields || [];
        const itemCustomizationValues = Object.fromEntries(itemCustomizationFields.map((f) => [f.key, f.value || '']));
        const itemSelectOtherActive = {};
        itemCustomizationFields.forEach((f) => {
          if (f.value_type === 'select' && f.allow_other && f.value && f.options && !f.options.includes(f.value)) {
            itemSelectOtherActive[f.key] = true;
          }
        });

        setFormItems((prevItems) => {
          const currentItem = prevItems.find((item) => item.id === formItemId);
          const finalPrice = currentItem?.unit_price || unitPrice;
          const finalDisplay = currentItem?.unit_priceDisplay || (finalPrice > 0 ? formatCurrency(finalPrice) : '');
          const persistedItemId = newItemRes.data?.id || null;

          const updated = prevItems.map((item) =>
            item.id === formItemId
              ? {
                  ...item,
                  rowKey: persistedItemId || item.rowKey,
                  itemId: persistedItemId,
                  lineId: targetLineId,
                  original_sku_id: formItem.sku_id,
                  unit_price: finalPrice,
                  unit_priceDisplay: finalDisplay,
                  customization_fields: itemCustomizationFields,
                  customizationValues: itemCustomizationValues,
                  selectOtherActive: itemSelectOtherActive,
                }
              : item
          );
          formItemsRef.current = updated;
          return updated;
        });
        queueSavedToast();
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error', autoHideDuration: 6000 });
    }
  };

  const handleRemoveFormItem = async (formItemId) => {
    const formItem = formItems.find((item) => item.id === formItemId);

    if (formItem?.itemId && formItem?.lineId && order?.id) {
      beginGlobal();
      try {
        await deleteOrderLineItem(order.id, formItem.lineId, formItem.itemId);
        await loadOrder();
      } catch (err) {
        setSnackbar({ open: true, message: err.message, severity: 'error', autoHideDuration: 6000 });
      } finally {
        endGlobal();
      }
      return;
    }
    
    if (formItemsRef.current.length > 1) {
      if (formItemSaveTimeoutsRef.current[formItemId]) {
        clearTimeout(formItemSaveTimeoutsRef.current[formItemId]);
        delete formItemSaveTimeoutsRef.current[formItemId];
      }
      setFormItems(prevItems => {
        const filtered = prevItems.filter(item => item.id !== formItemId);
        formItemsRef.current = filtered;
        return filtered;
      });
    }
  };

  /** Flush debounced line-item save when leaving SKU field (avoids race with tab to next field). */
  const flushFormItemSave = (formItemId) => {
    if (formItemSaveTimeoutsRef.current[formItemId]) {
      clearTimeout(formItemSaveTimeoutsRef.current[formItemId]);
      delete formItemSaveTimeoutsRef.current[formItemId];
      void handleSaveFormItem(formItemId);
    }
  };

  const handleUpdateFormItem = (formItemId, updates) => {
    const skuChanged = Object.prototype.hasOwnProperty.call(updates, 'sku_id');
    let shouldSaveSkuImmediately = false;
    const draftDesc = descriptionDraftByIdRef.current[formItemId];
    const mergedUpdates = { ...updates };
    if (draftDesc !== undefined && !Object.prototype.hasOwnProperty.call(updates, 'description')) {
      mergedUpdates.description = draftDesc;
    }

    setFormItems((prevItems) => {
      const updatedItems = prevItems.map((item) =>
        item.id === formItemId ? { ...item, ...mergedUpdates } : item
      );

      formItemsRef.current = updatedItems;

      if (formItemSaveTimeoutsRef.current[formItemId]) {
        clearTimeout(formItemSaveTimeoutsRef.current[formItemId]);
        delete formItemSaveTimeoutsRef.current[formItemId];
      }

      const updatedItem = updatedItems.find((item) => item.id === formItemId);
      const itemCanSave =
        updatedItem &&
        updatedItem.quantity >= 1 &&
        updatedItem.unit_price >= 0 &&
        (updatedItem.sku_id || updatedItem.description?.trim()) &&
        order?.id;

      if (itemCanSave) {
        if (skuChanged) {
          shouldSaveSkuImmediately = true;
        } else {
          formItemSaveTimeoutsRef.current[formItemId] = setTimeout(async () => {
            const currentItem = formItemsRef.current.find((item) => item.id === formItemId);
            const currentCanSave =
              currentItem &&
              currentItem.quantity >= 1 &&
              currentItem.unit_price >= 0 &&
              (currentItem.sku_id || currentItem.description?.trim());
            if (currentCanSave) {
              await handleSaveFormItem(formItemId);
            }
            delete formItemSaveTimeoutsRef.current[formItemId];
          }, 2000);
        }
      }

      return updatedItems;
    });

    if (shouldSaveSkuImmediately) {
      queueMicrotask(() => {
        void handleSaveFormItem(formItemId);
      });
    }
  };

  const handleUpdateFormItemRef = useRef(handleUpdateFormItem);
  handleUpdateFormItemRef.current = handleUpdateFormItem;
  const handleSaveFormItemRef = useRef(handleSaveFormItem);
  handleSaveFormItemRef.current = handleSaveFormItem;

  const onDescriptionDraftInput = useCallback((id, text) => {
    descriptionDraftByIdRef.current[id] = text;
  }, []);

  const onDescriptionCommit = useCallback((formItemId, text) => {
    delete descriptionDraftByIdRef.current[formItemId];
    handleUpdateFormItemRef.current(formItemId, { description: text });
    const currentItem = formItemsRef.current.find((item) => item.id === formItemId);
    const oid = orderRef.current?.id;
    const canSave =
      currentItem &&
      currentItem.quantity >= 1 &&
      currentItem.unit_price >= 0 &&
      (currentItem.sku_id || (currentItem.description || '').trim()) &&
      oid;
    if (canSave) {
      void handleSaveFormItemRef.current(formItemId);
    }
  }, []);

  const handleDeleteLine = async (lineId) => {
    if (!order?.id) return;
    beginGlobal();
    try {
      await deleteOrderLine(order.id, lineId);
      setSnackbar({ open: true, message: translate('orders.group_deleted'), severity: 'success', autoHideDuration: 6000 });
      setConfirmDelete({ open: false, type: null, lineId: null, itemId: null });
      loadOrder();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error', autoHideDuration: 6000 });
    } finally {
      endGlobal();
    }
  };

  const handleDeleteLineItem = async (lineId, itemId) => {
    if (!order?.id) return;
    beginGlobal();
    try {
      await deleteOrderLineItem(order.id, lineId, itemId);
      setSnackbar({ open: true, message: translate('orders.item_deleted'), severity: 'success', autoHideDuration: 6000 });
      setConfirmDelete({ open: false, type: null, lineId: null, itemId: null });
      await loadOrder();

      const updatedOrder = await getOrder(order.id);
      if (!updatedOrder.data?.lines || updatedOrder.data.lines.length === 0) {
        await addOrderLine(order.id, { sequence: 1, description: '' });
        await loadOrder();
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error', autoHideDuration: 6000 });
    } finally {
      endGlobal();
    }
  };


  const allSkus = useMemo(
    () =>
      products.flatMap((p) =>
        (skusByProduct[p.id] || []).map((s) => ({
          ...s,
          productName: p.name,
          productCode: p.code || '',
          productDescription: p.description || '',
          productType: p.type || 'PRODUCT',
          productStatus: p.status || 'ACTIVE',
          productImageUrl: productImageUrlById[p.id] || null,
        }))
      ),
    [products, skusByProduct, productImageUrlById]
  );

  const orderSkuPickerOptions = useMemo(
    () =>
      allSkus.filter((s) => {
        const t = s.productType || 'PRODUCT';
        const st = s.productStatus || 'ACTIVE';
        return (t === 'PRODUCT' || t === 'SERVICE') && st === 'ACTIVE';
      }),
    [allSkus]
  );

  const skuById = useMemo(() => {
    const m = new Map();
    for (let i = 0; i < allSkus.length; i += 1) {
      const s = allSkus[i];
      if (s && s.id != null) m.set(s.id, s);
    }
    return m;
  }, [allSkus]);

  const handleItemCustomizationValueChange = (formItemId, key, value, formItem, extraUpdates = {}) => {
    const itemId = formItem.itemId;
    const lineId = formItem.lineId;
    if (!itemId || !lineId || !order?.id) return;

    setFormItems((prev) => {
      const updated = prev.map((item) =>
        item.id === formItemId
          ? {
              ...item,
              ...extraUpdates,
              customizationValues: { ...(item.customizationValues || {}), [key]: value },
            }
          : item
      );
      formItemsRef.current = updated;
      return updated;
    });

    const timeoutKey = `${formItemId}_${key}`;
    if (itemCustomizationSaveTimeoutsRef.current[timeoutKey]) {
      clearTimeout(itemCustomizationSaveTimeoutsRef.current[timeoutKey]);
    }
    itemCustomizationSaveTimeoutsRef.current[timeoutKey] = setTimeout(async () => {
      const currentItem = formItemsRef.current.find((i) => i.id === formItemId);
      if (!currentItem?.itemId || !currentItem?.lineId) return;
      const fields = (currentItem.customization_fields || []).map((f) => ({
        key: f.key,
        name: f.name,
        value_type: f.value_type,
        value: (currentItem.customizationValues || {})[f.key] ?? '',
        hide_in_public_form: !!f.hide_in_public_form,
      }));
      try {
        await upsertOrderLineItemCustomizationFields(order.id, currentItem.lineId, currentItem.itemId, fields);
        queueSavedToast();
      } catch (err) {
        setSnackbar({ open: true, message: err.message, severity: 'error', autoHideDuration: 6000 });
      }
      delete itemCustomizationSaveTimeoutsRef.current[timeoutKey];
    }, 800);
  };

  useEffect(() => {
    const pathMerge = {};
    formItems.forEach((row) => {
      (row.customization_fields || []).forEach((field) => {
        if (field.value_type === 'image' && Array.isArray(field.image_assets)) {
          field.image_assets.forEach((a) => {
            if (a.id && a.url) pathMerge[a.id] = a.url;
          });
        }
      });
    });
    if (Object.keys(pathMerge).length === 0) return;
    setCustomizationMediaPathById((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(pathMerge).forEach((k) => {
        if (next[k] !== pathMerge[k]) {
          next[k] = pathMerge[k];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [formItems]);

  const openItemCustomizationImagePicker = (formItemId, key, formItem) => {
    itemCustomizationImageTargetRef.current = { formItemId, key, formItem };
    itemCustomizationImageInputRef.current?.click();
  };

  const handleItemCustomizationImageInputChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const t = itemCustomizationImageTargetRef.current;
    if (!file || !t.formItemId || !t.key || !t.formItem) return;
    const cur = parseCustomizationImageIds((t.formItem.customizationValues || {})[t.key]);
    if (cur.length >= 10) {
      setSnackbar({ open: true, message: 'Máximo de 10 imagens por campo', severity: 'warning', autoHideDuration: 6000 });
      return;
    }
    setCustomizationImageUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (order?.id) {
        fd.append('gallery_seed_order_id', order.id);
      }
      const res = await uploadMedia(fd);
      const row = res?.data;
      const mediaId = row?.id;
      const filePath = row?.file_path;
      if (!mediaId || !filePath) throw new Error('Upload falhou');
      setCustomizationMediaPathById((prev) => ({ ...prev, [mediaId]: filePath }));
      handleItemCustomizationValueChange(t.formItemId, t.key, stringifyCustomizationImageIds([...cur, mediaId]), t.formItem);
      setMediaRefreshKey((k) => k + 1);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Upload falhou', severity: 'error', autoHideDuration: 6000 });
    } finally {
      setCustomizationImageUploading(false);
    }
  };

  const handleItemCustomizationImageRemove = (formItemId, key, mediaId, formItem) => {
    const cur = parseCustomizationImageIds((formItem.customizationValues || {})[key]);
    const next = cur.filter((x) => x !== mediaId);
    handleItemCustomizationValueChange(formItemId, key, stringifyCustomizationImageIds(next), formItem);
  };

  const handleCommitDraftCustomField = async (formItemId, lid) => {
    const drafts = draftCustomFieldsRef.current[formItemId] || [];
    const draft = drafts.find((d) => d._lid === lid);
    if (!draft) return;
    const name = (draft.name || '').trim();
    if (!name || !order?.id) return;
    const formItem = formItemsRef.current.find((i) => i.id === formItemId);
    if (!formItem?.itemId || !formItem?.lineId) return;

    const baseKey = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '') || 'campo';
    const existingKeys = new Set((formItem.customization_fields || []).map((f) => f.key));
    let key = baseKey;
    let n = 1;
    while (existingKeys.has(key)) {
      key = `${baseKey}_${n++}`;
    }

    const value_type = draft.value_type || 'text';
    const value = value_type === 'image' ? '[]' : (draft.value ?? '');

    try {
      const currentFields = formItem.customization_fields || [];
      const fields = [
        ...currentFields.map((f) => ({
          key: f.key,
          name: f.name,
          value_type: f.value_type,
          value: (formItem.customizationValues || {})[f.key] ?? '',
          hide_in_public_form: !!f.hide_in_public_form,
        })),
        { key, name, value_type, value, hide_in_public_form: !!draft.hide_in_public_form },
      ];
      await upsertOrderLineItemCustomizationFields(order.id, formItem.lineId, formItem.itemId, fields);

      const res = await getOrder(order.id);
      if (res.data) {
        setOrder(res.data);
      }

      setDraftCustomFields((prev) => {
        const arr = (prev[formItemId] || []).filter((d) => d._lid !== lid);
        if (!arr.length) { const next = { ...prev }; delete next[formItemId]; return next; }
        return { ...prev, [formItemId]: arr };
      });

      const newItemFromOrder = orderLinesBySequence(res.data?.lines).flatMap((l) => l.items || []).find((i) => i.id === formItem.itemId);
      if (newItemFromOrder?.customization_fields) {
        const itemCustomizationValues = Object.fromEntries(newItemFromOrder.customization_fields.map((f) => [f.key, f.value || '']));
        setFormItems((prev) =>
          prev.map((it) =>
            it.id === formItemId
              ? { ...it, customization_fields: newItemFromOrder.customization_fields, customizationValues: itemCustomizationValues }
              : it
          )
        );
        formItemsRef.current = formItemsRef.current.map((it) =>
          it.id === formItemId ? { ...it, customization_fields: newItemFromOrder.customization_fields, customizationValues: itemCustomizationValues } : it
        );
      }
      queueSavedToast();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error', autoHideDuration: 6000 });
    }
  };

  const clearGhostCustomFieldCommitTimer = (formItemId, lid) => {
    const k = `${formItemId}_${lid}`;
    const t = ghostCustomFieldCommitTimersRef.current[k];
    if (t) {
      clearTimeout(t);
      delete ghostCustomFieldCommitTimersRef.current[k];
    }
  };

  const scheduleGhostCustomFieldCommit = (formItemId, lid) => {
    clearGhostCustomFieldCommitTimer(formItemId, lid);
    const k = `${formItemId}_${lid}`;
    ghostCustomFieldCommitTimersRef.current[k] = setTimeout(() => {
      delete ghostCustomFieldCommitTimersRef.current[k];
      handleCommitDraftCustomField(formItemId, lid);
    }, 320);
  };

  const addDraftRowForItem = (formItemId) => {
    const lid = `${Date.now()}${Math.random()}`;
    newDraftFocusLidRef.current = lid;
    setDraftCustomFields((prev) => ({
      ...prev,
      [formItemId]: [...(prev[formItemId] || []), { _lid: lid, ...DEFAULT_ITEM_CUSTOM_FIELD_DRAFT }],
    }));
  };

  const handleCustomFieldValueTabKeyDown = (formItemId) => (e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      addDraftRowForItem(formItemId);
    }
  };

  const handleItemCustomFieldMetaChange = (formItemId, fieldKey, updates) => {
    const applyUpdate = (it) => {
      if (it.id !== formItemId) return it;
      const prevField = (it.customization_fields || []).find((x) => x.key === fieldKey);
      const nextFields = (it.customization_fields || []).map((f) =>
        f.key === fieldKey ? { ...f, ...updates } : f
      );
      let nextValues = { ...(it.customizationValues || {}) };
      if (updates.value_type === 'image' && prevField && prevField.value_type !== 'image') {
        nextValues[fieldKey] = '[]';
      }
      if (updates.value_type && updates.value_type !== 'image' && prevField?.value_type === 'image') {
        nextValues[fieldKey] = '';
      }
      return { ...it, customization_fields: nextFields, customizationValues: nextValues };
    };
    setFormItems((prev) => prev.map(applyUpdate));
    formItemsRef.current = formItemsRef.current.map(applyUpdate);

    const timeoutKey = `meta_${formItemId}_${fieldKey}`;
    if (itemCustomFieldMetaSaveTimeoutsRef.current[timeoutKey]) {
      clearTimeout(itemCustomFieldMetaSaveTimeoutsRef.current[timeoutKey]);
    }
    itemCustomFieldMetaSaveTimeoutsRef.current[timeoutKey] = setTimeout(async () => {
      delete itemCustomFieldMetaSaveTimeoutsRef.current[timeoutKey];
      const currentItem = formItemsRef.current.find((i) => i.id === formItemId);
      if (!currentItem?.itemId || !currentItem?.lineId || !order?.id) return;
      const fields = (currentItem.customization_fields || []).map((f) => ({
        key: f.key,
        name: f.name,
        value_type: f.value_type,
        value: (currentItem.customizationValues || {})[f.key] ?? '',
        hide_in_public_form: !!f.hide_in_public_form,
      }));
      try {
        await upsertOrderLineItemCustomizationFields(order.id, currentItem.lineId, currentItem.itemId, fields);
        queueSavedToast();
      } catch (err) {
        setSnackbar({ open: true, message: err.message, severity: 'error', autoHideDuration: 6000 });
      }
    }, 800);
  };

  const handleToggleHideInPublicForm = async (formItemId, fieldKey, formItem) => {
    if (!formItem?.itemId || !formItem?.lineId || !order?.id) return;
    const currentFields = formItem.customization_fields || [];
    const updatedFields = currentFields.map((f) =>
      f.key === fieldKey ? { ...f, hide_in_public_form: !f.hide_in_public_form } : f
    );
    setFormItems((prev) =>
      prev.map((it) => (it.id === formItemId ? { ...it, customization_fields: updatedFields } : it))
    );
    formItemsRef.current = formItemsRef.current.map((it) =>
      it.id === formItemId ? { ...it, customization_fields: updatedFields } : it
    );
    const fields = updatedFields.map((f) => ({
      key: f.key,
      name: f.name,
      value_type: f.value_type,
      value: (formItem.customizationValues || {})[f.key] ?? '',
      hide_in_public_form: !!f.hide_in_public_form,
    }));
    try {
      await upsertOrderLineItemCustomizationFields(order.id, formItem.lineId, formItem.itemId, fields);
      queueSavedToast();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error', autoHideDuration: 6000 });
    }
  };

  const handleRemoveItemCustomField = async (formItemId, fieldId) => {
    const formItem = formItemsRef.current.find((i) => i.id === formItemId);
    if (!formItem?.itemId || !formItem?.lineId || !order?.id || !fieldId) return;
    setSavingItemId(formItemId);
    try {
      await deleteOrderLineItemCustomizationField(order.id, formItem.lineId, formItem.itemId, fieldId);
      await refreshOrderTimeline();
      const updatedFields = (formItem.customization_fields || []).filter((f) => f.id !== fieldId);
      const updatedValues = { ...(formItem.customizationValues || {}) };
      const removedField = (formItem.customization_fields || []).find((f) => f.id === fieldId);
      if (removedField) delete updatedValues[removedField.key];
      setFormItems((prev) =>
        prev.map((it) =>
          it.id === formItemId ? { ...it, customization_fields: updatedFields, customizationValues: updatedValues } : it
        )
      );
      formItemsRef.current = formItemsRef.current.map((it) =>
        it.id === formItemId ? { ...it, customization_fields: updatedFields, customizationValues: updatedValues } : it
      );
      queueSavedToast();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error', autoHideDuration: 6000 });
    } finally {
      setSavingItemId(null);
    }
  };

  const { subtotal, discountValue, finalValue, discountPercent } = useMemo(() => {
    const st = formItems.reduce((sum, item) => {
      const qty = item.quantity || 0;
      const price = item.unit_price || 0;
      return sum + qty * price;
    }, 0);
    const dv = formData.discount_value ?? 0;
    const fv = Math.max(0, st - dv);
    const dp = st > 0 ? (dv / st) * 100 : 0;
    return { subtotal: st, discountValue: dv, finalValue: fv, discountPercent: dp };
  }, [formItems, formData.discount_value]);

  const applyDiscountFromPercent = (pctStr) => {
    const parsed = parsePercent(pctStr);
    if (parsed === '' || pctStr.trim() === '') {
      setFormData((f) => ({ ...f, discount_value: null }));
      return;
    }
    const pct = parseFloat(parsed);
    const dv = Math.max(0, Math.min(subtotal, subtotal * (pct / 100)));
    setFormData((f) => ({ ...f, discount_value: dv }));
  };

  const applyDiscountFromValue = (valueStr) => {
    if (!valueStr || valueStr.trim() === '') {
      setFormData((f) => ({ ...f, discount_value: null }));
      return;
    }
    const parsed = parseCurrency(valueStr);
    if (parsed === '') {
      setFormData((f) => ({ ...f, discount_value: null }));
      return;
    }
    const num = parseFloat(parsed);
    const dv = Math.max(0, Math.min(subtotal, num));
    setFormData((f) => ({ ...f, discount_value: dv }));
  };

  const applyDiscountFromFinalValue = (valueStr) => {
    if (!valueStr || valueStr.trim() === '') {
      setFormData((f) => ({ ...f, discount_value: null }));
      return;
    }
    const parsed = parseCurrency(valueStr);
    if (parsed === '') {
      setFormData((f) => ({ ...f, discount_value: null }));
      return;
    }
    const num = parseFloat(parsed);
    const finalVal = Math.max(0, Math.min(subtotal, num));
    const dv = subtotal - finalVal;
    setFormData((f) => ({ ...f, discount_value: dv }));
  };

  const statusOptionKeys = {
    DRAFT: 'orders.draft',
    SENT: 'orders.sent',
    AWAITING_FORM: 'orders.awaiting_form',
    APPROVED: 'orders.approved',
    CONFIRMED: 'orders.confirmed',
    IN_PREPARATION: 'orders.in_preparation',
    DELIVERED: 'orders.delivered',
    REJECTED: 'orders.rejected',
    EXPIRED: 'orders.expired',
    CANCELLED: 'orders.cancelled',
  };
  const statusSelectResolved = useMemo(() => {
    const cur = formData.status;
    const raw = order?.status_options;
    let full;
    if (Array.isArray(raw) && raw.length > 0) {
      const has = raw.some((o) => String(o.id) === String(cur));
      full = has ? [...raw] : cur ? [...raw, { id: cur, name: order?.status_label || cur }] : [...raw];
    } else {
      full = LEGACY_STATUS_ORDER.map((sid) => ({
        id: sid,
        name: translate(statusOptionKeys[sid] || 'orders.status'),
      }));
    }
    const menuOptions = full.filter((o) => String(o.id) !== String(cur));
    return { full, menuOptions };
    // statusOptionKeys is static; formData.status and order drive updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, formData.status, translate]);
  const selectedStatusColor = useMemo(() => {
    if (!formData.status) return null;
    const opt = statusSelectResolved.full.find((o) => String(o.id) === String(formData.status));
    if (opt?.color) return opt.color;
    if (order?.status_color && String(formData.status) === String(order?.status)) return order.status_color;
    return null;
  }, [formData.status, statusSelectResolved, order]);
  const mockFieldLabelSx = {
    fontSize: '12px',
    color: '#5c6370',
    fontWeight: 500,
    lineHeight: 1.2,
  };
  const mockFieldWrapSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: '5px', md: '6px' },
  };
  const mockFieldInputSx = {
    '& .MuiInputBase-root': {
      minHeight: { xs: '44px', md: '40px' },
      borderRadius: '6px',
      fontSize: { xs: '16px', md: '13px' },
    },
    '& .MuiOutlinedInput-input': {
      py: { xs: '8px', md: '8px' },
      px: { xs: '11px', md: '10px' },
    },
  };
  const brDateWithPickerSx = { ...mockFieldInputSx, minWidth: { xs: '100%', md: 160 } };
  const renderBrDateWithCalendarField = ({ value, onBrChange, disabled, required, error, dataTestId }) => (
    <TextField
      size="small"
      required={!!required}
      disabled={!!disabled}
      value={value}
      onChange={(e) => {
        handleDateInputChange(e, (synth) => onBrChange(synth.target.value));
      }}
      placeholder="dd/mm/aaaa"
      inputProps={{ maxLength: 10, 'data-testid': dataTestId }}
      error={!!error}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              component="label"
              edge="end"
              size="small"
              disabled={!!disabled}
              sx={{ position: 'relative', mr: -0.5 }}
              aria-label={translate('orders.date_open_calendar')}
            >
              <CalendarMonthIcon fontSize="small" />
              <input
                type="date"
                disabled={!!disabled}
                style={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0,
                  width: '100%',
                  height: '100%',
                  cursor: !disabled ? 'pointer' : 'default',
                  margin: 0,
                }}
                value={displayBRToISO(value) || ''}
                onChange={(e) => {
                  const iso = e.target.value;
                  onBrChange(iso ? isoToDisplayBR(iso) : '');
                }}
              />
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={brDateWithPickerSx}
    />
  );

  const statusTimelineEvents = useMemo(() => buildOrderStatusTimelineEvents(order), [order]);

  const refreshOrderTimeline = useCallback(async () => {
    const oid = orderRef.current?.id;
    if (!oid) return;
    const res = await getOrder(oid);
    setOrder(res.data);
    setMediaRefreshKey((k) => k + 1);
  }, []);

  const importFromOrderDialog = (
    <Dialog open={importOrderDialogOpen} onClose={handleCloseImportDialog} fullWidth maxWidth="sm">
      <DialogTitle>{translate('orders.import_from_order_title')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {id ? (
          <Alert severity="info">{translate('orders.import_into_current_hint')}</Alert>
        ) : (
          <TextField
            select
            label={translate('orders.order_type')}
            value={importOrderTypeId || newOrderResolvedTypeIdRef.current || ''}
            onChange={(e) => setImportOrderTypeId(e.target.value)}
            size="small"
            fullWidth
          >
            {activeOrderTypes.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <OrderSearchAutocomplete
          value={importSourceOrder}
          onChange={setImportSourceOrder}
          excludeOrderId={id || undefined}
          label={translate('orders.import_source_order')}
        />
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }} component="p">
            {translate('orders.import_what_to_include')}
          </Typography>
          <Stack spacing={0.5}>
            <FormControlLabel control={<Checkbox size="small" checked={importIncLines} onChange={(e) => setImportIncLines(e.target.checked)} />} label={translate('orders.import_inc_lines')} />
            <FormControlLabel control={<Checkbox size="small" checked={importIncHistory} onChange={(e) => setImportIncHistory(e.target.checked)} />} label={translate('orders.import_inc_history')} />
            <FormControlLabel control={<Checkbox size="small" checked={importIncOrderMedia} onChange={(e) => setImportIncOrderMedia(e.target.checked)} />} label={translate('orders.import_inc_order_media')} />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={importIncHistoryMedia}
                  disabled={!importIncHistory}
                  onChange={(e) => setImportIncHistoryMedia(e.target.checked)}
                />
              }
              label={translate('orders.import_inc_history_media')}
            />
            <FormControlLabel control={<Checkbox size="small" checked={importIncObs} onChange={(e) => setImportIncObs(e.target.checked)} />} label={translate('orders.import_inc_observations')} />
            <FormControlLabel control={<Checkbox size="small" checked={importIncDisc} onChange={(e) => setImportIncDisc(e.target.checked)} />} label={translate('orders.import_inc_discount')} />
            <FormControlLabel control={<Checkbox size="small" checked={importIncTitle} onChange={(e) => setImportIncTitle(e.target.checked)} />} label={translate('orders.import_inc_title')} />
            <FormControlLabel control={<Checkbox size="small" checked={importIncCustom} onChange={(e) => setImportIncCustom(e.target.checked)} />} label={translate('orders.import_inc_customizations')} />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseImportDialog} disabled={importSubmitting}>
          {translate('common.cancel')}
        </Button>
        <Button variant="contained" onClick={submitImportOrder} disabled={importSubmitting}>
          {id ? translate('orders.import_apply') : translate('orders.import_create')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (creating) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>{translate('common.loading')}</Typography>
      </Box>
    );
  }

  if (!order && !id) {
    if (!newOrderTypesLoaded) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      );
    }
    if (activeOrderTypes.length === 0) {
      return (
        <Box sx={{ p: 3, maxWidth: 560 }}>
          <Alert severity="warning">{translate('orders.no_active_order_types')}</Alert>
        </Box>
      );
    }
    if (newOrderCreateError) {
      return (
        <Box sx={{ p: 3, maxWidth: 520 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {newOrderCreateError}
          </Alert>
          <Button
            variant="contained"
            onClick={() => {
              setNewOrderCreateError(null);
              void createOrderAutomaticallyRef.current(null, newOrderResolvedTypeIdRef.current);
            }}
          >
            {translate('orders.new_order_retry')}
          </Button>
        </Box>
      );
    }
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
        {importFromOrderDialog}
      </>
    );
  }

  if (!order && id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>{translate('common.loading')}</Typography>
      </Box>
    );
  }

  const paymentEntriesSummary = order?.payment_entries || [];
  const totalPaidSummary = paymentEntriesSummary.reduce((s, pe) => s + (Number(pe.amount) || 0), 0);
  const balanceAfterPaymentsSummary = Math.max(0, finalValue - totalPaidSummary);

  const closePaymentEntryDialog = () => setPaymentEntryDialog({ open: false, mode: 'create', entry: null });

  const notifyPayment = (message, severity) =>
    setSnackbar({
      open: true,
      message,
      severity,
      autoHideDuration: severity === 'error' ? 6000 : 3000,
    });

  const handlePaymentEntryFormSubmit = async ({ body, receiptFile: rf }) => {
    if (!order?.id) return;
    if (paymentEntryDialog.mode === 'create') {
      await createOrderPaymentEntry(order.id, body, rf);
    } else if (paymentEntryDialog.entry?.id) {
      await updateOrderPaymentEntry(order.id, paymentEntryDialog.entry.id, body, rf);
    }
    await refreshOrderTimeline();
    closePaymentEntryDialog();
    notifyPayment(translate('common.saved'), 'success');
  };

  const observationsPanel = (
    <Paper elevation={0} sx={{ ...orderMockCardSx, ...orderMockCardPadSx, ...orderRailObsGridSx }}>
      <Typography component="h2" sx={orderRailHeadingSx} id="order-rail-obs-h">
        {translate('orders.observations_rail')}
      </Typography>
      <TextField
        fullWidth
        multiline
        minRows={4}
        placeholder={translate('orders.observations_placeholder') || 'Anotações internas e histórico de conversa com o cliente'}
        value={formData.observations || ''}
        onChange={(e) => setFormData((f) => ({ ...f, observations: e.target.value }))}
        onBlur={() => {
          flushPendingOrderSave();
        }}
        inputProps={{ maxLength: 10000, 'aria-labelledby': 'order-rail-obs-h' }}
        size="small"
        variant="outlined"
        sx={{
          '& .MuiInputBase-root': {
            alignItems: 'flex-start',
            borderRadius: '8px',
            fontSize: { xs: '16px', md: '14px' },
            minHeight: { xs: '100px', md: '140px' },
          },
        }}
      />
    </Paper>
  );

  const summaryPanel = (
    <Paper elevation={0} sx={{ ...orderMockCardSx, ...orderMockCardPadSx, ...orderRailSummaryGridSx }}>
      <Typography component="h2" sx={orderRailHeadingSx} id="order-rail-sum-h">
        {translate('orders.order_summary')}
      </Typography>
      <Box sx={orderSummaryLinesSx}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <Typography component="span" sx={{ color: 'text.secondary' }}>
            {translate('orders.subtotal')}
          </Typography>
          <Typography component="span" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
            {formatCurrency(subtotal)}
          </Typography>
        </Box>
        <TextField
          size="small"
          label={translate('orders.discount_percent')}
          type="text"
          inputMode="decimal"
          value={discountFieldFocused === 'percent' ? discountPercentInput : (discountPercent > 0 ? formatPercent(discountPercent) : '')}
          onChange={(e) => handlePercentInputChange(e, (synth) => setDiscountPercentInput(synth.target?.value ?? ''))}
          onFocus={() => {
            setDiscountFieldFocused('percent');
            setDiscountPercentInput(discountPercent > 0 ? formatPercent(discountPercent) : '');
          }}
          onBlur={() => {
            applyDiscountFromPercent(discountPercentInput);
            setDiscountFieldFocused(null);
            setDiscountPercentInput('');
          }}
          placeholder="0"
          sx={{ width: '100%' }}
        />
        <TextField
          size="small"
          label={translate('orders.discount_value')}
          value={discountFieldFocused === 'value' ? discountValueInput : (discountValue > 0 ? formatCurrency(discountValue) : '')}
          onChange={(e) => handleCurrencyInputChange(e, (synth) => {
            if (typeof synth === 'string') {
              setDiscountValueInput('');
              return;
            }
            setDiscountValueInput(synth.target.value);
          })}
          onFocus={() => {
            setDiscountFieldFocused('value');
            setDiscountValueInput(discountValue > 0 ? formatCurrency(discountValue) : '');
          }}
          onBlur={() => {
            applyDiscountFromValue(discountValueInput);
            setDiscountFieldFocused(null);
            setDiscountValueInput('');
          }}
          placeholder="R$ 0,00"
          sx={{ width: '100%' }}
        />
        <TextField
          size="small"
          label={translate('orders.final_value')}
          value={discountFieldFocused === 'final' ? finalValueInput : formatCurrency(finalValue)}
          onChange={(e) => handleCurrencyInputChange(e, (synth) => {
            if (typeof synth === 'string') {
              setFinalValueInput('');
              return;
            }
            setFinalValueInput(synth.target.value);
          })}
          onFocus={() => {
            setDiscountFieldFocused('final');
            setFinalValueInput(formatCurrency(finalValue));
          }}
          onBlur={() => {
            applyDiscountFromFinalValue(finalValueInput);
            setDiscountFieldFocused(null);
            setFinalValueInput('');
          }}
          placeholder="R$ 0,00"
          sx={{ width: '100%' }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <Typography component="span" sx={{ color: 'text.secondary' }}>
            {translate('orders.order_total_after_discount')}
          </Typography>
          <Typography component="span" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
            {formatCurrency(finalValue)}
          </Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {translate('orders.payment_entries_title')}
        </Typography>
        {!order.id ? (
          <Typography variant="body2" color="text.secondary">
            {translate('orders.payment_entries_after_save')}
          </Typography>
        ) : (
          <>
            {paymentEntriesSummary.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {translate('orders.payment_entries_empty')}
              </Typography>
            ) : (
              paymentEntriesSummary.map((pe) => (
                <Box
                  key={pe.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.5,
                  }}
                >
                  <Typography component="span" variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                    {paymentMethodLabelFromValue(translate, pe.method)} · {formatDate(pe.paid_at)}
                  </Typography>
                  <Typography component="span" variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', color: 'success.dark' }}>
                    −{formatCurrency(pe.amount)}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label={translate('orders.payment_edit')}
                    onClick={() => setPaymentEntryDialog({ open: true, mode: 'edit', entry: pe })}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  {pe.receipt?.available && pe.receipt.download_path ? (
                    <IconButton
                      size="small"
                      aria-label={translate('orders.payment_view_receipt')}
                      onClick={() =>
                        openOrderPaymentEntryReceipt(order.id, pe.id).catch((err) =>
                          setSnackbar({ open: true, message: err.message, severity: 'error' }),
                        )
                      }
                    >
                      <ReceiptLongIcon fontSize="small" />
                    </IconButton>
                  ) : pe.receipt?.available && pe.receipt.legacy_file_path ? (
                    <IconButton
                      size="small"
                      component="a"
                      href={`${ORDER_FORM_API_BASE}${pe.receipt.legacy_file_path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={translate('orders.payment_view_receipt')}
                    >
                      <ReceiptLongIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </Box>
              ))
            )}
            <Button
              variant="outlined"
              size="small"
              fullWidth
              sx={{ mt: 1 }}
              startIcon={<AddIcon />}
              onClick={() => setPaymentEntryDialog({ open: true, mode: 'create', entry: null })}
            >
              {translate('orders.payment_add')}
            </Button>
          </>
        )}
        <Box sx={orderSummaryTotalSx}>
          <Typography component="span">{translate('orders.balance_remaining')}</Typography>
          <Typography component="span" color="primary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(balanceAfterPaymentsSummary)}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );

  const renderCustomerAutocompleteInput = (params) => (
    <TextField
      {...params}
      size="small"
      placeholder=""
      onBlur={(e) => {
        params.inputProps?.onBlur?.(e);
        window.setTimeout(() => {
          if (skipEnsureNextBlurRef.current) {
            skipEnsureNextBlurRef.current = false;
            return;
          }
          ensureCustomerOnBlur();
        }, 0);
      }}
    />
  );

  return (
    <Box sx={orderPageWrapSx}>
      <Box sx={orderLayoutGridSx}>
      <Box sx={orderMainColumnSx}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
          {order?.order_type_id ? (
            <Box
              component="button"
              type="button"
              aria-label={`${order.order_type_name || translate('orders.order_type')}: ${translate('orders.change_order_type')}`}
              onClick={() =>
                setOrderTypeChangeDialog({
                  open: true,
                  step: 1,
                  targetTypeId: '',
                  statusMap: {},
                  targetStatuses: [],
                  reason: 'change',
                })
              }
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                m: 0,
                p: 0,
                border: 'none',
                background: 'none',
                font: 'inherit',
                cursor: 'pointer',
                borderRadius: 1,
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }}
            >
              <Chip
                component="span"
                size="small"
                label={order.order_type_name || translate('orders.order_type')}
                variant="outlined"
                sx={{ cursor: 'inherit' }}
              />
              <KeyboardArrowDownIcon sx={{ fontSize: '1.125rem', color: 'action.active' }} aria-hidden />
            </Box>
          ) : null}
          {headerSaving ? (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 400, letterSpacing: 0.2 }}>
              {translate('common.saving')}
            </Typography>
          ) : null}
          {id && order?.id ? (
            <Button
              type="button"
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon fontSize="small" />}
              onClick={() => setImportOrderDialogOpen(true)}
              sx={{ ml: 'auto' }}
            >
              {translate('orders.import_from_open_order')}
            </Button>
          ) : null}
          <ExtensionSlot
            slotId="order.form.header"
            context={{ order, orderId: order?.id }}
          />
        </Box>

        <Paper elevation={0} sx={{ ...orderMockCardSx, ...orderMockCardPadSx, ...orderStackBelowSx }}>
        <Box sx={orderFormRowSx}>
          <Box sx={{ ...mockFieldWrapSx, minWidth: { md: 280 }, width: { xs: '100%', md: 'auto' }, flex: { md: '1 1 280px' } }}>
            <Typography component="label" sx={mockFieldLabelSx}>{translate('orders.customer')}</Typography>
            <Autocomplete
              freeSolo
              options={customers}
              getOptionLabel={getCustomerOptionLabel}
              isOptionEqualToValue={(opt, val) => {
                if (val == null) return opt == null;
                if (typeof val === 'string') return typeof opt === 'string' && val === opt;
                if (typeof val === 'object' && val?.id != null) {
                  return typeof opt === 'object' && opt?.id === val.id;
                }
                return false;
              }}
              componentsProps={{
                clearIndicator: {
                  onMouseDown: (e) => {
                    e.preventDefault();
                  },
                },
              }}
              value={
                customers.find((c) => c.id === formData.customer_id) ??
                (customerInputText.trim() ? customerInputText : null)
              }
              onChange={handleCustomerChange}
              onInputChange={(event, newInputValue, reason) => {
                setCustomerInputText(newInputValue ?? '');
                if (reason === 'clear' || (newInputValue ?? '').trim() === '') {
                  skipEnsureNextBlurRef.current = true;
                  setFormData((f) => ({ ...f, customer_id: '' }));
                  return;
                }
                skipEnsureNextBlurRef.current = false;
                setFormData((f) => {
                  const sel = customers.find((c) => c.id === f.customer_id);
                  if (!sel) return f;
                  const label = getCustomerOptionLabel(sel);
                  if (newInputValue !== label && newInputValue !== sel.name) {
                    return { ...f, customer_id: '' };
                  }
                  return f;
                });
              }}
              inputValue={customerInputText}
              sx={mockFieldInputSx}
              renderInput={renderCustomerAutocompleteInput}
            />
          </Box>
          <Box sx={{ ...mockFieldWrapSx, minWidth: { md: 220 }, width: { xs: '100%', md: 'auto' }, flex: { md: '1 1 220px' } }}>
            <Typography component="label" sx={mockFieldLabelSx}>{translate('orders.order_title')}</Typography>
            <TextField
              size="small"
              value={formData.title || ''}
              onChange={(e) => setFormData((f) => ({ ...f, title: e.target.value }))}
              onBlur={() => {
                flushPendingOrderSave();
              }}
              placeholder={translate('orders.order_title_placeholder') || 'Ex: miniatura boneco tamanho X'}
              inputProps={{ maxLength: 255 }}
              sx={mockFieldInputSx}
            />
          </Box>
          <Box sx={{ ...mockFieldWrapSx, minWidth: { md: 160 }, width: { xs: '100%', md: 'auto' }, flex: { md: '0 0 auto' } }}>
            <Typography component="label" sx={mockFieldLabelSx}>{translate('orders.valid_until')}</Typography>
            {renderBrDateWithCalendarField({
              value: formData.valid_until,
              onBrChange: (v) => setFormData((f) => ({ ...f, valid_until: v })),
              dataTestId: 'order-valid-until-date',
            })}
          </Box>
          <Box sx={{ ...mockFieldWrapSx, minWidth: { md: 160 }, width: { xs: '100%', md: 'auto' }, flex: { md: '0 0 auto' } }}>
            <Typography component="label" sx={mockFieldLabelSx}>{translate('orders.delivery_date')}</Typography>
            {renderBrDateWithCalendarField({
              value: formData.delivery_date,
              onBrChange: (v) => setFormData((f) => ({ ...f, delivery_date: v })),
              required: true,
              error: !formData.delivery_date?.trim(),
              dataTestId: 'order-delivery-date',
            })}
          </Box>
          <Box sx={{ ...mockFieldWrapSx, minWidth: { md: 200 }, width: { xs: '100%', md: 'auto' }, flex: { md: '0 0 auto' } }}>
            <Typography component="label" sx={mockFieldLabelSx}>{translate('orders.created_at')}</Typography>
            {renderBrDateWithCalendarField({
              value: formData.created_at,
              onBrChange: (v) => setFormData((f) => ({ ...f, created_at: v })),
              disabled: order ? !order.can_edit_created_at : !['DRAFT', 'SENT'].includes(formData.status),
              dataTestId: 'order-created-at',
            })}
          </Box>
          <Box sx={{ ...mockFieldWrapSx, minWidth: { md: 160 }, width: { xs: '100%', md: 'auto' }, flex: { md: '0 0 auto' } }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.25 }}>
              <Typography component="label" sx={mockFieldLabelSx}>
                {translate('orders.status')}
              </Typography>
              {order?.order_type_id ? (
                <IconButton
                  type="button"
                  size="small"
                  aria-label={translate('orders.status_flow_info_aria')}
                  onClick={() => void handleOpenStatusFlow()}
                  sx={{ p: 0.25, color: 'action.active' }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              ) : null}
            </Box>
            <TextField
              size="small"
              select
              value={formData.status}
              onChange={(e) => setFormData((f) => ({ ...f, status: e.target.value }))}
              sx={{
                ...mockFieldInputSx,
                ...(selectedStatusColor && {
                  '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                    borderColor: selectedStatusColor,
                    borderWidth: 2,
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: selectedStatusColor,
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: selectedStatusColor,
                  },
                }),
              }}
              SelectProps={{
                SelectDisplayProps: { 'data-testid': 'order-status-select' },
                renderValue: (v) => {
                  const opt = statusSelectResolved.full.find((o) => String(o.id) === String(v));
                  if (opt?.name) return opt.name;
                  return translate(statusOptionKeys[v] || 'orders.status');
                },
              }}
            >
              {statusSelectResolved.menuOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          {order?.id ? (
            <Box
              sx={{
                ...mockFieldWrapSx,
                minWidth: { md: 'auto' },
                width: { xs: '100%', md: 'auto' },
                flex: { md: '0 0 auto' },
              }}
            >
              <Typography component="label" sx={{ ...mockFieldLabelSx, visibility: { md: 'hidden' }, minHeight: { md: '1.25rem' } }}>
                {'\u00a0'}
              </Typography>
              <Chip
                icon={<PhotoLibraryIcon />}
                label={translate('orders.gallery_manage_chip')}
                onClick={() => isEdit && setOrderGalleryDialogOpen(true)}
                variant="outlined"
                color="primary"
                disabled={!isEdit}
                clickable={isEdit}
              />
            </Box>
          ) : null}
          {order?.show_customization_form_for_status && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={async () => {
                try {
                  if (autoSaveQuickTimeoutRef.current) {
                    clearTimeout(autoSaveQuickTimeoutRef.current);
                    autoSaveQuickTimeoutRef.current = null;
                  }
                  if (autoSaveTextTimeoutRef.current) {
                    clearTimeout(autoSaveTextTimeoutRef.current);
                    autoSaveTextTimeoutRef.current = null;
                  }
                  await persistOrderAfterEdit();
                  const res = await getOrder(order.id);
                  const token = res?.data?.form_token;
                  const wsId = currentWorkspace?.id;
                  if (!token) {
                    setSnackbar({ open: true, message: translate('orders.save_first_for_link') || 'Salve o pedido para gerar o link.', severity: 'info' });
                  } else if (!wsId) {
                    setSnackbar({ open: true, message: translate('orders.select_workspace_for_link'), severity: 'info' });
                  } else {
                    const url = `${window.location.origin}/form/${wsId}/${token}`;
                    await navigator.clipboard.writeText(url);
                    setSnackbar({ open: true, message: translate('orders.form_link_copied') || 'Link copiado!', severity: 'success' });
                    setOrder((o) => (o ? { ...o, form_token: token } : o));
                  }
                } catch (err) {
                  setSnackbar({ open: true, message: err.message, severity: 'error' });
                }
              }}
            >
              {translate('orders.copy_form_link') || 'Copiar link do formulário'}
            </Button>
          )}
        </Box>
      </Paper>

      {isEdit && order?.id && (
        <Paper component="section" elevation={0} sx={orderStatusTransitionCardSx}>
          <Box sx={orderStatusTransitionHeaderRowSx}>
            <Typography component="h2" sx={orderStatusTransitionTitleSx}>
              {translate('orders.status_transition_section_title')}
            </Typography>
            <Chip size="small" label={translate('orders.status_transition_draft_badge')} sx={orderStatusTransitionDraftChipSx} />
          </Box>
          <Typography component="p" variant="body2" sx={orderStatusTransitionHintSx}>
            {translate('orders.status_transition_section_hint')}
          </Typography>
          <TextField
            id="order-status-transition-draft-note"
            fullWidth
            multiline
            minRows={3}
            size="small"
            label={translate('orders.status_transition_note_label')}
            value={statusTransitionNote}
            onChange={(e) => {
              const v = e.target.value;
              statusTransitionNoteRef.current = v;
              setStatusTransitionNote(v);
            }}
            onBlur={() => {
              flushPendingOrderSave();
            }}
            sx={{ mb: 1 }}
          />
          <MediaGallery
            metadataKey="order_status_draft"
            metadataValue={order.id}
            accept="*/*"
            hideEmptyPlaceholder
            refreshKey={draftMediaRefreshKey}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              size="small"
              variant="text"
              onClick={handleSaveHistoryNow}
              disabled={globalBusy || savingHistory}
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
                textTransform: 'none',
                minWidth: 'auto',
                px: 1,
              }}
              startIcon={savingHistory ? <CircularProgress size={12} sx={{ color: 'text.secondary' }} /> : null}
            >
              {translate('orders.save_history_button')}
            </Button>
          </Box>
        </Paper>
      )}

      <Paper elevation={0} sx={{ ...orderMockCardSx, ...orderMockCardPadSx, ...orderStackBelowSx }}>
        <Typography component="h2" sx={orderSectionTitleSx}>
          {translate('orders.add_item')}
        </Typography>
        <Box sx={orderItemsToolbarSx}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNewFormLine} disabled={globalBusy}>
            {translate('orders.add_new_line')}
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {formItems.map((formItem) => (
            <InlineSavingOverlay
              key={formItem.rowKey ?? formItem.id}
              data-order-form-item={formItem.rowKey ?? formItem.id}
              open={savingItemId === formItem.id}
              sx={orderItemRowSx}
            >
              <Box sx={orderItemGridSx}>
              <Box className="order-item-desc" sx={orderItemDescFieldSx}>
              <Typography component="label" sx={mockFieldLabelSx}>{translate('orders.item_description') || 'Descrição/Observação'}</Typography>
              <OrderFormItemDescriptionField
                formItemId={formItem.id}
                committedDescription={formItem.description}
                disabled={savingItemId === formItem.id}
                placeholder={
                  formItem.sku_id
                    ? translate('orders.item_description_placeholder') || 'Observações adicionais (opcional)'
                    : translate('orders.item_description_required_free') || 'Descrição obrigatória para itens sem produto'
                }
                onDraftInput={onDescriptionDraftInput}
                onCommit={onDescriptionCommit}
                sx={{ ...mockFieldInputSx, width: '100%' }}
              />
              </Box>
              <Box className="order-item-qty" sx={orderItemQtyFieldSx}>
              <Typography component="label" sx={mockFieldLabelSx}>{translate('orders.quantity')}</Typography>
              <TextField
                size="small"
                type="number"
                value={formItem.quantity}
                disabled={savingItemId === formItem.id}
                onChange={(e) => handleUpdateFormItem(formItem.id, { quantity: parseInt(e.target.value, 10) || 1 })}
                onBlur={() => {
                  const currentItem = formItemsRef.current.find(item => item.id === formItem.id);
                  const canSave = currentItem && currentItem.quantity >= 1 && currentItem.unit_price >= 0 && (currentItem.sku_id || currentItem.description?.trim()) && order?.id;
                  if (canSave) {
                    handleSaveFormItem(formItem.id);
                  }
                }}
                sx={{ ...mockFieldInputSx, width: '100%' }}
                inputProps={{ min: 1 }}
              />
              </Box>
              <Box className="order-item-price" sx={orderItemPriceFieldSx}>
              <Typography component="label" sx={mockFieldLabelSx}>{translate('orders.unit_price')}</Typography>
              <TextField
                size="small"
                value={formItem.unit_priceDisplay || ''}
                disabled={savingItemId === formItem.id}
                onChange={(e) => {
                  handleCurrencyInputChange(e, (synth) => {
                    if (typeof synth === 'string') {
                      handleUpdateFormItem(formItem.id, { unit_price: 0, unit_priceDisplay: '' });
                    } else {
                      handleUpdateFormItem(formItem.id, { unit_price: synth.numericValue, unit_priceDisplay: synth.target.value });
                    }
                  });
                }}
                onFocus={() => {
                  if (!formItem.unit_priceDisplay && formItem.unit_price > 0) {
                    const displayValue = formatCurrency(formItem.unit_price);
                    handleUpdateFormItem(formItem.id, { unit_priceDisplay: displayValue });
                  }
                }}
                onBlur={() => {
                  if (formItem.unit_priceDisplay && formItem.unit_priceDisplay.trim() !== '') {
                    const numericValue = parseFloat(formItem.unit_priceDisplay.replace(/[^\d,]/g, '').replace(',', '.')) || 0;
                    if (numericValue > 0) {
                      const formattedDisplay = formatCurrency(numericValue);
                      if (formItem.unit_priceDisplay !== formattedDisplay) {
                        handleUpdateFormItem(formItem.id, { unit_price: numericValue, unit_priceDisplay: formattedDisplay });
                      } else if (numericValue !== formItem.unit_price) {
                        handleUpdateFormItem(formItem.id, { unit_price: numericValue });
                      }
                    }
                  } else if (formItem.unit_price > 0) {
                    handleUpdateFormItem(formItem.id, { unit_priceDisplay: formatCurrency(formItem.unit_price) });
                  }
                  const currentItem = formItemsRef.current.find(item => item.id === formItem.id);
                  const canSave = currentItem && currentItem.quantity >= 1 && currentItem.unit_price >= 0 && (currentItem.sku_id || currentItem.description?.trim()) && order?.id;
                  if (canSave) {
                    handleSaveFormItem(formItem.id);
                  }
                }}
                sx={{ ...mockFieldInputSx, width: '100%' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ ml: 0, maxHeight: 32 }}>
                      <ExtensionSlot
                        slotId="order.item.price"
                        context={{
                          description: formItem.description || '',
                          quantity: formItem.quantity || 1,
                          onSetPrice: (price) => handleUpdateFormItem(formItem.id, {
                            unit_price: price,
                            unit_priceDisplay: formatCurrency(price),
                          }),
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              </Box>
              <Box className="order-item-sku" sx={orderItemSkuFieldSx}>
              <Typography component="label" sx={mockFieldLabelSx}>{translate('orders.sku_optional')}</Typography>
              <Autocomplete
                disabled={savingItemId === formItem.id}
                options={orderSkuPickerOptions}
                filterOptions={filterOrderSkuAutocompleteOptions}
                getOptionLabel={skuAutocompleteOptionLabel}
                isOptionEqualToValue={skuAutocompleteIsOptionEqual}
                value={formItem.sku_id ? skuById.get(formItem.sku_id) ?? null : null}
                onChange={(_, option) => {
                  const currentItem = formItemsRef.current.find(item => item.id === formItem.id);
                  const hasCustomPrice = currentItem && (
                    (currentItem.unit_priceDisplay && currentItem.unit_priceDisplay !== '') ||
                    (currentItem.unit_price > 0 && (!currentItem.unit_priceDisplay || currentItem.unit_priceDisplay === ''))
                  );
                  
                  if (hasCustomPrice && currentItem.unit_price > 0) {
                    handleUpdateFormItem(formItem.id, {
                      sku_id: option?.id || '',
                    });
                  } else {
                    handleUpdateFormItem(formItem.id, {
                      sku_id: option?.id || '',
                      unit_price: option?.price || 0,
                      unit_priceDisplay: option?.price ? formatCurrency(option.price) : ''
                    });
                  }
                }}
                sx={mockFieldInputSx}
                renderOption={(props, option) => {
                  const { key, ...rest } = props;
                  const thumb = option.productImageUrl || PRODUCT_DEFAULT_IMAGE;
                  return (
                    <Box component="li" key={key} {...rest} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                      <Avatar variant="rounded" src={thumb} alt="" sx={{ width: 40, height: 40, flexShrink: 0 }} />
                      <Typography component="span" variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                        {skuAutocompleteOptionLabel(option)}
                      </Typography>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Código ou busca"
                    inputProps={{
                      ...params.inputProps,
                      onBlur: (e) => {
                        params.inputProps?.onBlur?.(e);
                        flushFormItemSave(formItem.id);
                      },
                    }}
                  />
                )}
              />
              </Box>
              <Box className="order-item-del">
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleRemoveFormItem(formItem.id)}
                disabled={globalBusy || savingItemId === formItem.id || (formItems.length === 1 && !formItem.itemId)}
                title={translate('common.delete')}
              >
                {translate('common.delete')}
              </Button>
              </Box>
            </Box>
            {formItem.itemId && (
              <Box sx={{ mt: 1.5 }}>
                {/* ── Header ── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {translate('orders.customizations') || 'Customizações'}
                    {(formItem.customization_fields?.length || 0) > 0 && ` (${formItem.customization_fields.length})`}
                  </Typography>
                  <IconButton
                    size="small"
                    disabled={savingItemId === formItem.id}
                    title={translate('orders.add_custom_field') || 'Adicionar campo'}
                    onClick={() => addDraftRowForItem(formItem.id)}
                    sx={{ p: '2px' }}
                  >
                    <AddIcon sx={{ fontSize: '0.95rem' }} />
                  </IconButton>
                </Box>

                {/* ── Table ── */}
                {((formItem.customization_fields?.length || 0) > 0 || (draftCustomFields[formItem.id]?.length || 0) > 0) && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) 108px minmax(0, 1fr) 68px 32px',
                      alignItems: 'start',
                      columnGap: 0.75,
                      rowGap: 0.5,
                    }}
                  >
                    {/* Column headers */}
                    <Typography variant="caption" color="text.disabled" sx={{ px: 0.25 }}>
                      {translate('orders.field_name') || 'Nome'}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {translate('orders.field_type') || 'Tipo'}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {translate('orders.custom_field_value_placeholder') || 'Valor'}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'center' }}>
                      {translate('orders.hide_in_public_form_short') || 'Ocultar'}
                    </Typography>
                    <Box />

                    {/* Existing fields */}
                    {(formItem.customization_fields || []).map((f, fIdx) => (
                      <React.Fragment key={f.id || f.key}>
                        <TextField
                          size="small"
                          value={f.name}
                          onChange={(e) => handleItemCustomFieldMetaChange(formItem.id, f.key, { name: e.target.value })}
                          disabled={savingItemId === formItem.id}
                          sx={CF_INPUT_SX}
                        />
                        <TextField
                          size="small"
                          select
                          value={f.value_type}
                          onChange={(e) => handleItemCustomFieldMetaChange(formItem.id, f.key, { value_type: e.target.value })}
                          disabled={savingItemId === formItem.id}
                          SelectProps={{ MenuProps: { disablePortal: true } }}
                          sx={CF_INPUT_SX}
                        >
                          <MenuItem value="text">{translate('orders.field_type_text') || 'Texto'}</MenuItem>
                          <MenuItem value="number">{translate('orders.field_type_number') || 'Número'}</MenuItem>
                          <MenuItem value="date">{translate('orders.field_type_date') || 'Data'}</MenuItem>
                          <MenuItem value="textarea">{translate('orders.field_type_textarea') || 'Texto longo'}</MenuItem>
                          <MenuItem value="image">{translate('orders.field_type_image') || 'Imagem'}</MenuItem>
                        </TextField>
                        {f.value_type === 'textarea' ? (
                          <TextField
                            size="small"
                            multiline
                            rows={2}
                            value={(formItem.customizationValues || {})[f.key] || ''}
                            onChange={(e) => handleItemCustomizationValueChange(formItem.id, f.key, e.target.value, formItem)}
                            sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
                          />
                        ) : f.value_type === 'image' ? (
                          <Box
                            sx={{
                              minWidth: 0,
                              maxWidth: '100%',
                              border: '2px solid',
                              borderColor: 'primary.main',
                              borderRadius: 1,
                              p: 1,
                              bgcolor: 'action.hover',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                              minHeight: 72,
                              boxSizing: 'border-box',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', minWidth: 0 }}>
                              <ImageOutlinedIcon color="primary" sx={{ fontSize: '1.1rem', flexShrink: 0 }} />
                              <Typography
                                variant="caption"
                                color="primary"
                                fontWeight={600}
                                sx={{ flex: '1 1 auto', minWidth: 0 }}
                              >
                                {translate('orders.customization_images') || 'Imagens'}
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={
                                  customizationImageUploading ||
                                  savingItemId === formItem.id ||
                                  parseCustomizationImageIds((formItem.customizationValues || {})[f.key]).length >= 10
                                }
                                onClick={() => openItemCustomizationImagePicker(formItem.id, f.key, formItem)}
                                sx={{ flexShrink: 0 }}
                              >
                                {translate('common.upload') || 'Enviar'}
                              </Button>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'flex-start' }}>
                              {parseCustomizationImageIds((formItem.customizationValues || {})[f.key]).map((mid) => {
                                const path =
                                  (f.image_assets || []).find((a) => a.id === mid)?.url || customizationMediaPathById[mid];
                                const src = path
                                  ? `${ORDER_FORM_API_BASE}${path.startsWith('/') ? path : `/${path}`}`
                                  : '';
                                const lbItems = buildCustomizationImageLightboxItems(
                                  f,
                                  (formItem.customizationValues || {})[f.key],
                                  customizationMediaPathById
                                );
                                const openPreview = () => {
                                  if (!lbItems.length) return;
                                  const active = lbItems.find((m) => m.id === mid) || lbItems[0];
                                  setCustomizationImageLightbox({ open: true, items: lbItems, active });
                                };
                                return (
                                  <Box
                                    key={mid}
                                    role="button"
                                    tabIndex={0}
                                    onClick={openPreview}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openPreview();
                                      }
                                    }}
                                    title={translate('common.view')}
                                    sx={{
                                      position: 'relative',
                                      width: 56,
                                      height: 56,
                                      flexShrink: 0,
                                      borderRadius: 0.5,
                                      overflow: 'hidden',
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      bgcolor: 'background.paper',
                                      cursor: src ? 'pointer' : 'default',
                                    }}
                                  >
                                    {src ? (
                                      <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                      <Box
                                        sx={{
                                          width: '100%',
                                          height: '100%',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}
                                      >
                                        <ImageOutlinedIcon fontSize="small" color="disabled" />
                                      </Box>
                                    )}
                                    <IconButton
                                      size="small"
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        p: 0.25,
                                        bgcolor: 'background.paper',
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleItemCustomizationImageRemove(formItem.id, f.key, mid, formItem);
                                      }}
                                      title={translate('orders.remove_image') || 'Remover'}
                                    >
                                      <DeleteIcon sx={{ fontSize: '0.85rem' }} />
                                    </IconButton>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        ) : f.value_type === 'select' && f.options && f.options.length > 0 ? (
                          <TextField
                            size="small"
                            select
                            value={
                              f.allow_other &&
                              ((formItem.selectOtherActive || {})[f.key] ||
                                ((formItem.customizationValues || {})[f.key] &&
                                  !(f.options || []).includes((formItem.customizationValues || {})[f.key])))
                                ? '__outro__'
                                : (formItem.customizationValues || {})[f.key] || ''
                            }
                            onChange={(e) => {
                              const v = e.target.value;
                              const opts = f.options || [];
                              const currentVal = (formItem.customizationValues || {})[f.key] || '';
                              if (v === '__outro__') {
                                handleItemCustomizationValueChange(formItem.id, f.key, currentVal && !opts.includes(currentVal) ? currentVal : '', formItem, { selectOtherActive: { ...(formItem.selectOtherActive || {}), [f.key]: true } });
                              } else {
                                handleItemCustomizationValueChange(formItem.id, f.key, v, formItem, { selectOtherActive: { ...(formItem.selectOtherActive || {}), [f.key]: false } });
                              }
                            }}
                            SelectProps={{ MenuProps: { disablePortal: true } }}
                            onKeyDown={
                              fIdx === (formItem.customization_fields?.length || 0) - 1 && !(draftCustomFields[formItem.id]?.length)
                                ? handleCustomFieldValueTabKeyDown(formItem.id)
                                : undefined
                            }
                            sx={CF_INPUT_SX}
                          >
                            <MenuItem value="">{translate('common.select') || 'Selecione'}</MenuItem>
                            {(f.options || []).map((opt) => (
                              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                            {f.allow_other && <MenuItem value="__outro__">{translate('orders.other') || 'Outro'}</MenuItem>}
                          </TextField>
                        ) : (
                          <TextField
                            size="small"
                            type={f.value_type === 'number' ? 'number' : f.value_type === 'date' ? 'date' : 'text'}
                            value={(formItem.customizationValues || {})[f.key] || ''}
                            onChange={(e) => handleItemCustomizationValueChange(formItem.id, f.key, e.target.value, formItem)}
                            InputLabelProps={f.value_type === 'date' ? { shrink: true } : undefined}
                            onKeyDown={
                              fIdx === (formItem.customization_fields?.length || 0) - 1 && !(draftCustomFields[formItem.id]?.length)
                                ? handleCustomFieldValueTabKeyDown(formItem.id)
                                : undefined
                            }
                            sx={CF_INPUT_SX}
                          />
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Checkbox
                            size="small"
                            checked={!!f.hide_in_public_form}
                            onChange={() => handleToggleHideInPublicForm(formItem.id, f.key, formItem)}
                            title={translate('orders.hide_in_public_form') || 'Ocultar no formulário público'}
                            tabIndex={-1}
                            sx={{ p: '2px' }}
                          />
                        </Box>
                        {!f.product_id && f.id ? (
                          <IconButton size="small" onClick={() => handleRemoveItemCustomField(formItem.id, f.id)} title={translate('common.delete')} tabIndex={-1} sx={{ p: '2px' }}>
                            <DeleteIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        ) : (
                          <Box />
                        )}
                      </React.Fragment>
                    ))}

                    {/* Draft (ghost) rows */}
                    {(draftCustomFields[formItem.id] || []).map((draft, dIdx) => {
                      const updateDraft = (patch) =>
                        setDraftCustomFields((prev) => ({
                          ...prev,
                          [formItem.id]: (prev[formItem.id] || []).map((d) =>
                            d._lid === draft._lid ? { ...d, ...patch } : d
                          ),
                        }));
                      return (
                        <React.Fragment key={draft._lid}>
                          <TextField
                            size="small"
                            placeholder={translate('orders.field_name') || 'Nome do campo'}
                            value={draft.name}
                            onChange={(e) => updateDraft({ name: e.target.value })}
                            onFocus={() => clearGhostCustomFieldCommitTimer(formItem.id, draft._lid)}
                            onBlur={() => scheduleGhostCustomFieldCommit(formItem.id, draft._lid)}
                            disabled={savingItemId === formItem.id}
                            sx={CF_INPUT_SX}
                            inputRef={(el) => {
                              if (el && newDraftFocusLidRef.current === draft._lid) {
                                el.focus();
                                newDraftFocusLidRef.current = null;
                              }
                            }}
                          />
                          <TextField
                            size="small"
                            select
                            value={draft.value_type}
                            onChange={(e) => {
                              const vt = e.target.value;
                              updateDraft({
                                value_type: vt,
                                value: vt === 'image' ? '' : draft.value,
                              });
                            }}
                            onFocus={() => clearGhostCustomFieldCommitTimer(formItem.id, draft._lid)}
                            onBlur={() => scheduleGhostCustomFieldCommit(formItem.id, draft._lid)}
                            disabled={savingItemId === formItem.id}
                            SelectProps={{ MenuProps: { disablePortal: true } }}
                            sx={CF_INPUT_SX}
                          >
                            <MenuItem value="text">{translate('orders.field_type_text') || 'Texto'}</MenuItem>
                            <MenuItem value="number">{translate('orders.field_type_number') || 'Número'}</MenuItem>
                            <MenuItem value="date">{translate('orders.field_type_date') || 'Data'}</MenuItem>
                            <MenuItem value="textarea">{translate('orders.field_type_textarea') || 'Texto longo'}</MenuItem>
                            <MenuItem value="image">{translate('orders.field_type_image') || 'Imagem'}</MenuItem>
                          </TextField>
                          {draft.value_type === 'image' ? (
                            <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, alignSelf: 'center' }}>
                              —
                            </Typography>
                          ) : (
                          <TextField
                            size="small"
                            placeholder={translate('orders.custom_field_value_placeholder') || 'Valor'}
                            value={draft.value}
                            onChange={(e) => updateDraft({ value: e.target.value })}
                            onFocus={() => clearGhostCustomFieldCommitTimer(formItem.id, draft._lid)}
                            onBlur={() => scheduleGhostCustomFieldCommit(formItem.id, draft._lid)}
                            onKeyDown={
                              dIdx === (draftCustomFields[formItem.id] || []).length - 1
                                ? (e) => {
                                    if (e.key === 'Tab' && !e.shiftKey) {
                                      e.preventDefault();
                                      scheduleGhostCustomFieldCommit(formItem.id, draft._lid);
                                      addDraftRowForItem(formItem.id);
                                    }
                                  }
                                : undefined
                            }
                            disabled={savingItemId === formItem.id}
                            sx={CF_INPUT_SX}
                          />
                          )}
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Checkbox
                              size="small"
                              checked={!!draft.hide_in_public_form}
                              onChange={() => updateDraft({ hide_in_public_form: !draft.hide_in_public_form })}
                              title={translate('orders.hide_in_public_form') || 'Ocultar no formulário público'}
                              tabIndex={-1}
                              sx={{ p: '2px' }}
                            />
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setDraftCustomFields((prev) => {
                                const arr = (prev[formItem.id] || []).filter((d) => d._lid !== draft._lid);
                                if (!arr.length) { const next = { ...prev }; delete next[formItem.id]; return next; }
                                return { ...prev, [formItem.id]: arr };
                              })
                            }
                            title={translate('common.cancel')}
                            tabIndex={-1}
                            sx={{ p: '2px' }}
                          >
                            <DeleteIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </React.Fragment>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
            </InlineSavingOverlay>
          ))}
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          ...orderTimelineSectionSx,
          ...orderStackBelowSx,
        }}
      >
        <Box sx={orderTimelineHeaderSx}>
          <Typography component="h2" sx={orderTimelineTitleSx}>
            {translate('orders.attachments_timeline_title')}
          </Typography>
        </Box>
        <Box sx={orderTimelineIngestOuterSx}>
          {order.id ? (
            <>
              <MediaGallery
                metadataKey="order_attachment"
                metadataValue={order.id}
                accept="*/*"
                readOnly
                hideEmptyReadOnly
                refreshKey={mediaRefreshKey}
              />
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {translate('orders.attachments_after_save')}
            </Typography>
          )}
        </Box>
        <OrderHistoryTimeline
          events={statusTimelineEvents}
          translate={translate}
          statusKeyMap={statusOptionKeys}
          mediaRefreshKey={mediaRefreshKey}
          editable={Boolean(isEdit && order?.id)}
          orderId={order?.id || null}
          onAfterMutate={refreshOrderTimeline}
          onDeleteHistoryBlock={(historyId) => setConfirmHistoryBlockDelete({ open: true, historyId })}
        />
      </Paper>

      <Dialog open={paymentEntryDialog.open} onClose={closePaymentEntryDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {paymentEntryDialog.mode === 'create'
            ? translate('orders.payment_add')
            : translate('orders.payment_edit')}
        </DialogTitle>
        <DialogContent>
          <OrderPaymentEntryForm
            key={
              paymentEntryDialog.mode === 'edit' && paymentEntryDialog.entry
                ? paymentEntryDialog.entry.id
                : 'payment-new'
            }
            mode={paymentEntryDialog.mode}
            entry={paymentEntryDialog.entry}
            orderId={order.id}
            translate={translate}
            notify={notifyPayment}
            onSubmit={handlePaymentEntryFormSubmit}
            onCancel={closePaymentEntryDialog}
            onRequestDelete={
              paymentEntryDialog.mode === 'edit' && paymentEntryDialog.entry
                ? () => {
                    const eid = paymentEntryDialog.entry.id;
                    closePaymentEntryDialog();
                    setConfirmPaymentEntryDelete({ open: true, entryId: eid });
                  }
                : undefined
            }
          />
        </DialogContent>
      </Dialog>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
          {order?.can_soft_delete ? (
            <Button color="error" variant="outlined" onClick={() => setConfirmSoftDeleteOrder(true)}>
              {translate('orders.soft_delete_from_list')}
            </Button>
          ) : null}
          <Button variant="outlined" onClick={() => navigate('/orders')}>
            {translate('orders.back_to_list')}
          </Button>
        </Stack>
      </Box>

      <Box sx={orderSideRailSx}>
        {observationsPanel}
        {summaryPanel}
      </Box>
      </Box>

      <SavingOverlay open={globalBusy} />

      <ConfirmDialog
        open={confirmDelete.open}
        title={translate('common.confirm_delete_title')}
        message={confirmDelete.type === 'line' ? (translate('orders.confirm_delete_group') || 'Remover este grupo e todos os itens?') : translate('orders.confirm_delete_item')}
        onConfirm={() => {
          if (confirmDelete.type === 'line' && confirmDelete.lineId) handleDeleteLine(confirmDelete.lineId);
          if (confirmDelete.type === 'item' && confirmDelete.lineId && confirmDelete.itemId) handleDeleteLineItem(confirmDelete.lineId, confirmDelete.itemId);
        }}
        onCancel={() => setConfirmDelete({ open: false, type: null, lineId: null, itemId: null })}
      />
      <ConfirmDialog
        open={confirmHistoryBlockDelete.open}
        title={translate('common.confirm_delete_title')}
        message={translate('orders.confirm_delete_history_block')}
        onConfirm={async () => {
          const hid = confirmHistoryBlockDelete.historyId;
          setConfirmHistoryBlockDelete({ open: false, historyId: null });
          if (!hid || !order?.id) return;
          try {
            await deleteOrderStatusHistory(order.id, hid);
            const res = await getOrder(order.id);
            setOrder(res.data);
            setMediaRefreshKey((k) => k + 1);
            setSnackbar({ open: true, message: translate('orders.history_block_deleted'), severity: 'success' });
          } catch (e) {
            setSnackbar({
              open: true,
              message: e.message || translate('orders.history_block_delete_error'),
              severity: 'error',
            });
          }
        }}
        onCancel={() => setConfirmHistoryBlockDelete({ open: false, historyId: null })}
      />
      <ConfirmDialog
        open={confirmPaymentEntryDelete.open}
        title={translate('common.confirm_delete_title')}
        message={translate('orders.payment_confirm_delete')}
        onConfirm={async () => {
          const eid = confirmPaymentEntryDelete.entryId;
          setConfirmPaymentEntryDelete({ open: false, entryId: null });
          if (!eid || !order?.id) return;
          try {
            await deleteOrderPaymentEntry(order.id, eid);
            const res = await getOrder(order.id);
            setOrder(res.data);
            setSnackbar({ open: true, message: translate('common.saved'), severity: 'success', autoHideDuration: 3000 });
          } catch (e) {
            setSnackbar({ open: true, message: e.message || translate('common.error'), severity: 'error' });
          }
        }}
        onCancel={() => setConfirmPaymentEntryDelete({ open: false, entryId: null })}
      />
      <ConfirmDialog
        open={confirmSoftDeleteOrder}
        title={translate('common.confirm_delete_title')}
        message={translate('orders.confirm_soft_delete_order')}
        onConfirm={async () => {
          setConfirmSoftDeleteOrder(false);
          if (!order?.id) return;
          try {
            await softDeleteOrder(order.id);
            setSnackbar({
              open: true,
              message: translate('orders.soft_delete_success'),
              severity: 'success',
              autoHideDuration: 4000,
            });
            navigate('/orders');
          } catch (e) {
            setSnackbar({
              open: true,
              message: e.message || translate('common.error'),
              severity: 'error',
              autoHideDuration: 6000,
            });
          }
        }}
        onCancel={() => setConfirmSoftDeleteOrder(false)}
      />
      <Dialog
        open={orderTypeChangeDialog.open}
        onClose={(_, reason) => {
          if (orderTypeChangeDialog.reason === 'assign') return;
          if (reason === 'backdropClick') return;
          setOrderTypeChangeDialog({ ...initialOrderTypeChangeDialog });
        }}
        disableEscapeKeyDown={orderTypeChangeDialog.reason === 'assign'}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {orderTypeChangeDialog.reason === 'assign'
            ? translate('orders.assign_order_type_title')
            : translate('orders.change_order_type_title')}
        </DialogTitle>
        <DialogContent>
          {orderTypeChangeDialog.step === 1 ? (
            typesForOrderTypeChangeDialog.length === 0 ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {translate(
                  orderTypeChangeDialog.reason === 'assign' ? 'orders.no_active_order_types' : 'orders.no_other_order_types'
                )}
              </Alert>
            ) : (
              <TextField
                select
                fullWidth
                label={translate('orders.order_type')}
                value={orderTypeChangeDialog.targetTypeId}
                onChange={(e) => setOrderTypeChangeDialog((d) => ({ ...d, targetTypeId: e.target.value }))}
                sx={{ mt: 1 }}
              >
                {typesForOrderTypeChangeDialog.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </TextField>
            )
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {translate('orders.change_type_map_help')}
              </Typography>
              {collectDistinctStatusIdsFromOrder(order).map((sid) => (
                <Box
                  key={sid}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1,
                    alignItems: { sm: 'center' },
                  }}
                >
                  <Typography sx={{ minWidth: { sm: 200 } }} variant="body2">
                    {sourceStatusLabelForMap(order, sid)}
                  </Typography>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label={translate('orders.change_type_target_status')}
                    value={orderTypeChangeDialog.statusMap[sid] || ''}
                    onChange={(e) =>
                      setOrderTypeChangeDialog((d) => ({
                        ...d,
                        statusMap: { ...d.statusMap, [sid]: e.target.value },
                      }))
                    }
                  >
                    {orderTypeChangeDialog.targetStatuses.map((ts) => (
                      <MenuItem key={ts.status_definition_id} value={ts.status_definition_id}>
                        {ts.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {orderTypeChangeDialog.step === 2 ? (
            <Button onClick={() => setOrderTypeChangeDialog((d) => ({ ...d, step: 1 }))}>{translate('common.back')}</Button>
          ) : null}
          {orderTypeChangeDialog.reason === 'change' ? (
            <Button onClick={() => setOrderTypeChangeDialog({ ...initialOrderTypeChangeDialog })}>{translate('common.cancel')}</Button>
          ) : null}
          {orderTypeChangeDialog.step === 1 ? (
            <Button
              variant="contained"
              disabled={typesForOrderTypeChangeDialog.length === 0}
              onClick={() => advanceOrderTypeDialogToStep2()}
            >
              {translate('common.next')}
            </Button>
          ) : (
            <Button variant="contained" onClick={() => submitOrderTypeChange()}>
              {translate('common.save')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={statusFlowOpen}
        onClose={() => {
          setStatusFlowOpen(false);
          setStatusFlowError(null);
        }}
        fullScreen
      >
        <DialogTitle>
          {translate('orders.status_flow_dialog_title')}
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1.5 }}>
            — {order?.order_type_name || '—'}
          </Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}
        >
          {statusFlowError ? (
            <Box sx={{ px: 2, py: 2 }}>
              <Alert severity="error">{statusFlowError}</Alert>
            </Box>
          ) : (
            <Box sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <WorkflowEditorPage typeId={order?.order_type_id} readOnly />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusFlowOpen(false)}>{translate('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {importFromOrderDialog}

      <OrderGalleryManagerDialog
        open={orderGalleryDialogOpen}
        onClose={() => setOrderGalleryDialogOpen(false)}
        orderId={order?.id}
        order={order}
        formItems={formItems}
        canEdit={isEdit}
        onAfterSave={() => {
          setMediaRefreshKey((k) => k + 1);
          void refreshOrderTimeline();
        }}
      />

      <MediaLightbox
        open={customizationImageLightbox.open}
        onClose={() => setCustomizationImageLightbox((s) => ({ ...s, open: false }))}
        items={customizationImageLightbox.items}
        activeMedia={customizationImageLightbox.active}
        onNavigate={(m) => setCustomizationImageLightbox((s) => ({ ...s, active: m }))}
        apiBaseUrl={ORDER_FORM_API_BASE}
      />

      <input
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        ref={itemCustomizationImageInputRef}
        onChange={handleItemCustomizationImageInputChange}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration ?? 6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderFormPage;
